import i18n from '../i18n';

/**
 * Help content model:
 * - Each entry has default `title` / `overview` / `steps` (admin-oriented).
 * - `byRole` is an optional object keyed by user.role. When the current user
 *   matches a key, those fields override the default ones.
 * - Admin-only pages have no `byRole` because non-admins cannot reach them.
 */
const helpContent = {
  en: {
    '/': {
      title: 'Operations Dashboard',
      overview: 'Central hub for monitoring all aviation training operations.',
      steps: [
        'View key metrics: students, staff, aircraft, and compliance status.',
        'Medical Compliance card shows expiring certificates with color-coded urgency.',
        'Quick links to navigate to frequently used modules.'
      ]
    },
    '/students': {
      title: 'Student Management',
      overview: 'Manage students, medical certificates, documents, program enrollment, course progress, classes, compliance, and certification eligibility.',
      steps: [
        'Click "Register Student" to create a new student with personal info (English + Arabic names), medical certificate, and licensing details.',
        'Student profile tabs: Personal, Contact, Medical, Licensing, Flight Experience, Language, Program Enrollment, Course Progress, Classes, Security, Financial, Documents.',
        'Program Enrollment tab: Enroll in a program — system auto-enrolls in all required courses. NEW Certification Eligibility Checklist shows a 7-stage lifecycle rail (Not Started → In Training → Exams Complete → Checkride Ready → Checkride Passed → Graduated → Certificate Issued) with per-requirement pass/fail: ground hours, flight hours, exams, checkride, attendance, financial clearance.',
        'Per-course certificate grid shows which courses already have certificates — "Issue Program Diploma" becomes available only when every mandatory course is certified AND all gates are green.',
        'Course Progress tab: Unified overview, attendance, weighted grades, module completion, and exam eligibility. Below it, "Exam Papers" lets you attach scanned answer sheets (PDF/JPG/PNG) to the student — they appear instantly in the student portal under Grades & Exams.',
        'Header "Record violation" button: log a dress-code, lateness, absence or conduct violation for this student without leaving the profile — the student is notified and it shows in their Conduct tab.',
        'Classes tab: See all classes and assign to new ones directly.'
      ],
      byRole: {
        instructor: {
          overview: 'Browse the students assigned to you and review their profiles, progress, flight experience, and certification readiness.',
          steps: [
            'Use the search bar and filters to locate one of your assigned students.',
            'Click a student row to open the profile — tabs are read-only for instructors.',
            'Use Flight Experience and Course Progress tabs to review training history before a lesson.',
            'Completion Checklist on the Program Enrollment tab tells you at a glance what\'s blocking certification.'
          ]
        }
      }
    },
    '/courses': {
      title: 'Courses',
      overview: 'Manage courses with attendance thresholds, weighted grade formulas, and student progress tracking.',
      steps: [
        'Click "+ Add Course" to create with title, code, authority, hours, and syllabus modules.',
        'Configure "Attendance & Grading Policy": min attendance % and grade weights.',
        'Click bar chart icon for Progress Overlay — enrolled students, attendance, scores, sessions.',
        'Students below min attendance are blocked from midterm/final/practical exams.'
      ],
      byRole: {
        student: {
          overview: 'Browse the courses you are enrolled in and check attendance, grades, and syllabus progress.',
          steps: [
            'Use the search bar to find a specific course you are taking.',
            'Open a course to see its syllabus, modules, and your attendance percentage.',
            'If your attendance is below the policy threshold, you will be blocked from the final exam — contact your instructor.'
          ]
        }
      }
    },
    '/classes': {
      title: 'Classes & Schedule',
      overview: 'Manage class sessions as cohorts. Batch enroll, record attendance, generate recurring schedules, and view calendar.',
      steps: [
        'Toggle Grid/Calendar views. Calendar has Week and Month modes with drag-to-reschedule.',
        'Create a class: select Course, Program (Cohort), instructor, date/time, and Student Roster.',
        '"Enroll All" batch-enrolls all roster students in the linked program + required courses.',
        '"Attendance" opens batch attendance with duplicate detection.',
        '"Schedule" generates recurring sessions — pick days, time, date range, skip holidays.'
      ],
      byRole: {
        student: {
          overview: 'View the ground-school classes you are scheduled for.',
          steps: [
            'Use the Calendar view (Week or Month) to see upcoming classes.',
            'Click a class pill to see details: course, instructor, location and time.',
            'If you need to be excused, submit an absence request via the Student Portal.'
          ]
        }
      }
    },
    '/exams': {
      title: 'Examinations',
      overview: 'Manage exams with course-filtered batch results, attendance eligibility gate, and auto pass/fail.',
      steps: [
        'Create exams linked to a course — controls which students appear in results.',
        'Click "Results" for batch entry. Filter by: All, Eligible, Blocked, Class, Batch, Program.',
        'Enter scores inline — live percentage and PASS/FAIL. Blocked students have disabled inputs.',
        'Each existing result row has a "Request change" pencil icon (admin / instructor / chief_instructor) — opens the audit-logged grade-change workflow modal. Admins keep the direct Delete action.'
      ]
    },
    '/programs': {
      title: 'Training Programs',
      overview: 'GACA Part-141 programs with required-course mapping, hour reconciliation, and completion gates.',
      steps: [
        'Click "+ Add Program" to create with code, category, regulatory authority, duration, fees.',
        'Link required courses in the Course Matrix — students auto-enroll when they join the program.',
        'NEW Completion & Certification Gates panel: set Minimum Attendance % (GACA typical 80%), Requires Skill Test, Requires Checkride — these drive the certification eligibility engine.',
        'An amber health-dot appears on any program whose course hours don\'t reconcile with the program totals.',
        'Expand programs to manage Stages and Lessons.'
      ]
    },
    '/scheduling': {
      title: 'Scheduling',
      overview: 'Calendar views for flight schedule, instructor availability, and class schedule.',
      steps: [
        'Tabs: Schedule (flights), Availability (instructors), Classes (ground school calendar).',
        'Classes tab shows color-coded class pills. Drag to reschedule.'
      ],
      byRole: {
        student: {
          overview: 'View your personal training schedule — flights and ground classes side-by-side.',
          steps: [
            'Switch tabs to see your flight schedule or ground-school classes.',
            'Click any entry for the full details: time, instructor, location.',
            'You cannot drag-to-reschedule — contact your instructor to request a change.'
          ]
        },
        instructor: {
          overview: 'Your flight and ground-school calendar. Update your availability and review assigned sessions.',
          steps: [
            'Use the Availability tab to block out time you are unavailable.',
            'Schedule tab shows all flight slots you are dispatched on.',
            'Classes tab shows the ground-school sessions you are teaching.'
          ]
        }
      }
    },
    '/my-portal': {
      title: 'Student Portal',
      overview: 'Personal dashboard showing training progress, class schedule, flights, and finances.',
      steps: [
        'Overview tab: Onboarding checklist, program enrollment, financial summary.',
        'My Schedule tab: Weekly calendar with ground classes + flight training.',
        'Training, Billing, Documents, and Exams tabs for detailed views.'
      ]
    },
    '/analytics': {
      title: 'Analytics',
      overview: 'Advanced analytics with charts, revenue forecasting, and student performance predictions.',
      steps: [
        'Overview tab: Key performance indicators and trend charts.',
        'Revenue tab: Revenue by program, monthly trends, forecasting.',
        'Student Predictions: AI-powered risk analysis for student performance.',
        'Instructor Workload & Aircraft Utilization analysis.'
      ]
    },
    '/staff': {
      title: 'Staff Directory',
      overview: 'Manage all staff members including instructors, admin, and finance personnel.',
      steps: [
        'Click "Add Staff" to register a new employee with role, department, and contact details.',
        'View/edit staff profiles with license, medical, contract, and GOSI information.',
        'Filter staff by department, role, or status.'
      ]
    },
    '/student-progress': {
      title: 'Student Progress',
      overview: 'Track student flight hours, training stages, and program completion.',
      steps: [
        'View detailed flight hour logs per student.',
        'Track progress through training stages and milestones.',
        'Monitor attendance and performance across programs.'
      ]
    },
    '/skill-tests': {
      title: 'Skill Tests',
      overview: 'Practical skill evaluations and check rides for flight training.',
      steps: [
        'Create skill test templates with evaluation criteria.',
        'Record test results with detailed scoring.',
        'Track student readiness for check rides and practical exams.'
      ]
    },
    '/certifications': {
      title: 'Certifications',
      overview: 'Issue course certificates, program graduation diplomas, and track renewals through the full GACA compliance cycle.',
      steps: [
        'Issue a Course Certificate from a student\'s profile once the Completion Checklist turns green — the eligibility engine blocks issuance if hours, exams, checkride, attendance, or financial clearance are outstanding. Admins can "Force Issue" with a signed justification (audit-logged).',
        'Issue a Program Graduation Diploma only after every mandatory course in the program has its own certificate. The diploma stamps the enrollment as "completed" and renders a distinct landscape A4 diploma PDF.',
        'Renewal chain: when a cert is renewed, a fresh RENEW-YYYY-XXX certificate is created and the old one is marked "renewed" — full audit chain preserved.',
        'Dashboard widget "Certificate Renewals" (daily scan at 05:15 UTC) flags certs expiring in 90/60/30 days and creates a compliance task at 30 days. One-click Renew from the widget.',
        'Students can also upload external aviation certificates for verification.'
      ],
      byRole: {
        student: {
          overview: 'Upload your external aviation certificates and see the status of certificates issued to you — plus upcoming renewal reminders.',
          steps: [
            'Click "Upload Certificate" to submit an external credential for verification.',
            'Check the validity / expiry date of every credential. Notifications fire 90, 60, and 30 days before expiry.',
            'Certificates issued by the school after program completion appear here automatically.',
            'When a certificate is renewed, the previous one moves to "Superseded" and the new one (RENEW-...) takes its place.'
          ]
        }
      }
    },
    '/aircraft': {
      title: 'Aircraft Fleet',
      overview: 'Manage aircraft fleet with registration, maintenance status, and availability tracking.',
      steps: [
        'Add aircraft with registration, type, manufacturer, and seating capacity.',
        'Track maintenance schedules and airworthiness status.',
        'Monitor aircraft availability for flight dispatch.'
      ]
    },
    '/flight-logs': {
      title: 'Flight Logs',
      overview: 'Record and manage flight operations with detailed logging.',
      steps: [
        'Log flights with PIC, student, aircraft, route, and duration.',
        'Track flight hours by instructor and student.',
        'Generate flight hour reports for regulatory compliance.',
        'Each graded flight log row exposes a "Request change" pencil icon — opens the grade-change workflow modal (the only way to amend sealed Tier-4 logs).'
      ],
      byRole: {
        instructor: {
          overview: 'Log the flights you instruct and review your instructor flight-hour totals.',
          steps: [
            'Click "Log Flight" to record a new flight with PIC, student, aircraft, route and time.',
            'The entry feeds into your monthly instructor hours automatically.',
            'Use filters to review past flights for a specific student before a lesson.',
            'Once a flight has been graded, the "Request change" icon appears — use it instead of trying to edit directly (sealed logs cannot be edited any other way).'
          ]
        },
        student: {
          overview: 'Your personal logbook of every flight you have trained on.',
          steps: [
            'This page is read-only — your instructor logs flights after each lesson.',
            'Use the download button to export your logbook as PDF for record-keeping.',
            'If a flight entry looks wrong, contact your instructor to correct it.'
          ]
        }
      }
    },
    '/flight-dispatch': {
      title: 'Flight Dispatch',
      overview: 'Dispatch board for booking flight slots and managing daily operations.',
      steps: [
        'View the dispatch board with time slots and aircraft.',
        'Book new flights by selecting aircraft, instructor, student, and time.',
        'Check weather conditions and NOTAMs before dispatch.'
      ],
      byRole: {
        instructor: {
          overview: 'Coordinate your flight slots — see aircraft assigned, students booked, and jump straight into logging.',
          steps: [
            'Review today\'s flight slots assigned to you on the dispatch board.',
            'Click "Log flight" on a completed slot to prefill the flight log form.',
            'Always verify METAR / NOTAMs on the Weather page before the flight.'
          ]
        }
      }
    },
    '/maintenance': {
      title: 'Maintenance',
      overview: 'Aircraft maintenance tracking with scheduled and unscheduled work orders.',
      steps: [
        'Create maintenance work orders with priority and description.',
        'Track maintenance status: pending, in-progress, completed.',
        'Monitor aircraft downtime and return-to-service dates.'
      ]
    },
    '/weather': {
      title: 'Weather & NOTAMs',
      overview: 'Real-time weather data (METAR/TAF) and Notices to Airmen for flight planning.',
      steps: [
        'View current METAR and TAF for configured airports.',
        'Check active NOTAMs affecting flight operations.',
        'Weather data automatically factors into dispatch decisions.'
      ],
      byRole: {
        instructor: {
          overview: 'Pre-flight METAR, TAF and NOTAM briefing for your day\'s operations.',
          steps: [
            'Search by ICAO or click a quick-pick airport.',
            'Review the METAR, TAF forecast and any active NOTAMs before departure.',
            'Use this page for go / no-go decisions on training flights.'
          ]
        },
        student: {
          overview: 'Learn to read real METAR and TAF reports and check NOTAMs before your training flight.',
          steps: [
            'Enter the ICAO of your departure or destination airport.',
            'Review the raw METAR and TAF — these match the format used in GACA exams.',
            'Check NOTAMs to understand what is closed or restricted at the airport.'
          ]
        }
      }
    },
    '/safety': {
      title: 'Safety (SMS)',
      overview: 'Safety Management System for reporting and tracking safety incidents.',
      steps: [
        'Submit safety reports for incidents, hazards, and near-misses.',
        'Track investigation status and corrective actions.',
        'View safety trends and risk analysis dashboards.'
      ]
    },
    '/billing': {
      title: 'Billing & Invoices',
      overview: 'Create and manage student invoices with multi-currency support, VAT calculation, and ZATCA compliance.',
      steps: [
        'Create invoices with line items, VAT auto-calculation, and discounts.',
        'Currency dropdown: SAR / USD / EUR / GBP / ZAR. A live-FX preview shows "1 X = Y SAR" and the SAR equivalent — the ledger always posts SAR from the locked equivalent.',
        'Lock FX rate: tick "Lock FX rate (contract)" for scholarship/contract programmes and optionally set a valid-until date. Expired locks auto-fall-back to live.',
        'Invoice PDF renders native + SAR columns side-by-side for foreign-currency bills, with an FX footnote showing rate + source + validity.',
        'Track payment status: unpaid, partial, paid. Row-flash lands you on the exact invoice when arriving from the FX Drift widget.'
      ],
      byRole: {
        student: {
          overview: 'View the invoices the school has issued to you and your payment status. Foreign-currency invoices show both native and SAR totals.',
          steps: [
            'Open an invoice to see line items, VAT and the total due.',
            'Download the PDF copy — non-SAR invoices display native + SAR side-by-side.',
            'For a payment dispute, contact Finance via Admin & Communications.'
          ]
        }
      }
    },
    '/expenses': {
      title: 'Expenses',
      overview: 'Track and manage organizational expenses with receipt uploads and approval workflow.',
      steps: [
        'Submit expenses with category, amount, VAT, and receipt attachment.',
        'Approval workflow: pending → approved/rejected.',
        'Filter by category, status, and date range.'
      ]
    },
    '/budgets': {
      title: 'Budgets',
      overview: 'Budget planning and tracking by department and fiscal year.',
      steps: [
        'Create budgets with category allocations (fuel, maintenance, salaries, etc.).',
        'Set alert thresholds for budget warnings.',
        'Track actual spend vs. budget with variance analysis.'
      ]
    },
    '/payroll': {
      title: 'Payroll',
      overview: 'Staff salary management and payroll processing.',
      steps: [
        'View staff salary details and allowances.',
        'Process monthly payroll with deductions and net calculations.',
        'Export payroll reports.'
      ]
    },
    '/vendors': {
      title: 'Vendors',
      overview: 'Manage vendor directory for procurement and services.',
      steps: [
        'Add vendors with contact details, services, and payment terms.',
        'Track vendor contracts and performance.',
        'Link vendors to purchase orders.'
      ]
    },
    '/procurement': {
      title: 'Procurement',
      overview: 'Purchase order management and requisition tracking.',
      steps: [
        'Create purchase orders with line items and vendor selection.',
        'Track PO status: draft, approved, ordered, received.',
        'Link POs to budget categories for spend tracking.'
      ]
    },
    '/vat-reports': {
      title: 'VAT & ZATCA',
      overview: 'Tax reporting and ZATCA Phase 2 e-invoicing compliance.',
      steps: [
        'Generate VAT return reports for filing.',
        'View ZATCA-compliant invoice XML generation.',
        'Track VAT collected vs. VAT paid.'
      ]
    },
    '/finance-reports': {
      title: 'Finance Reports',
      overview: 'Financial summaries including P&L, receivables, and revenue analysis.',
      steps: [
        'View profit & loss summary.',
        'Track accounts receivable and aging.',
        'Revenue breakdown by program and period.'
      ]
    },
    '/student-finance': {
      title: 'Student Finance',
      overview: 'Student billing overview with payment tracking.',
      steps: [
        'View all student accounts and balances.',
        'Track payment history and outstanding amounts.',
        'Send payment reminders.'
      ]
    },
    '/fee-calculator': {
      title: 'Fee Calculator',
      overview: 'Estimate training program fees for prospective students.',
      steps: [
        'Select a program to calculate total fees.',
        'View fee breakdown: tuition, flight hours, materials, exams.',
        'Generate fee quotes for students.'
      ]
    },
    '/payment-plans': {
      title: 'Payment Plans',
      overview: 'Create and manage installment payment plans for students.',
      steps: [
        'Create payment plans with installment amounts and due dates.',
        'Track installment payments and overdue amounts.',
        'Automated reminders for upcoming payments.'
      ]
    },
    '/income-statement': {
      title: 'Income Statement',
      overview: 'Standard income statement: Revenue, Cost of Operations, Gross Profit, Operating Expenses, Net Income.',
      steps: [
        'Select period: Monthly, Quarterly, or Custom date range.',
        'Revenue from invoices (all non-cancelled). Expenses from approved expense records.',
        'Add custom entries: Revenue, Cost of Operations, or Operating Expenses.',
        'Download as PDF or print with company branding.',
        'Payroll estimate calculated from Salary Scale data.'
      ]
    },
    '/departments': {
      title: 'Departments',
      overview: 'Manage organizational departments with head assignment and staff tracking.',
      steps: [
        'Click "Add Department" to create with name, description, head, and status.',
        'Edit existing departments — change name, assign department head, set active/inactive.',
        'View staff count per department. Delete with safety warning if staff assigned.'
      ]
    },
    '/grade-change-requests': {
      title: 'Grade Change Requests',
      overview: 'Tier-3 / Tier-4 grade-change approval workflow for flight logs, exam results, and skill tests. Admins / Chief Instructors approve, instructors submit, and every action is audit-logged for GACA Part 141 / FERPA compliance.',
      steps: [
        'Submitters (admin / instructor) — click "+ New request" in the page header to open the picker modal. Pick a Record Type (Exam result / Skill test / Flight log), select the specific record from the dropdown (scoped to your students), propose the corrected values, choose a reason category and write a justification (≥10 chars).',
        'You can also request a change from the row-level "Request change" button on each record in Instructor Portal → Results & Grades → View Recorded, on /exams → Results tab, or on Flight Logs (graded rows only).',
        'The Student column shows the affected student\'s name and academic number for every request, so reviewers can confirm the target without opening another tab.',
        'Approvers — click Approve to apply the change. The change is written through the normal grade_history audit trail with your name as approver. Click Deny to refuse — review notes are mandatory and shown to the submitter.',
        'Click Edit on a pending row to revise the proposal (proposed values, reason, or category) before approving — audit-logged. Submitters cannot edit their own requests.',
        'Use the "Approver inbox / My requests" toggle (admin / chief_instructor only) to switch between the inbox and your own submissions. Non-approver roles auto-land on My requests.',
        'Self-approval is blocked: you cannot approve your own request even as admin (separation of duties).',
        'Affected students receive an in-app FERPA-style amendment notification the moment a request is applied — deep-linked to their portal.',
        'Sidebar badge on the "Grade Change Requests" entry shows pending count for approvers (excludes your own submissions). Refreshes every 60s.'
      ]
    },
    '/invoice-dunning': {
      title: 'Invoice Dunning',
      overview: 'Automated overdue-invoice reminders that fire at 7, 14, and 30 days past due. Each milestone produces an in-app notification (visible to admin + finance) and a SendGrid email to the student if SendGrid is configured. Idempotent per (invoice, tier) — the same 7d/14d/30d milestone is never re-dispatched.',
      steps: [
        'Three stat cards show total overdue invoices, pending dispatches (invoices that have crossed a new milestone but have not yet been notified), and the sum of outstanding balances.',
        'The table lists every overdue invoice with current tier (7d/14d/30d), days overdue, the milestones already dispatched, and the next required action.',
        'Click "Run scan now" to manually flush a backlog without waiting for the 04:30 UTC cron. Audit-logged with your user ID.',
        'Absence-fine invoices are excluded — they have a dedicated scanner with surcharge logic.',
        'Email delivery requires a SendGrid API key in Integration Settings. Without it, only in-app notifications fire.'
      ]
    },
    '/weather-minima': {
      title: 'Weather Minima Settings',
      overview: 'Configure the stage-specific weather minima used by the Flight Dispatch gate. When a flight slot has a `weather_minima_stage` assigned, the dispatch endpoint pulls the live METAR and blocks dispatch if any threshold is exceeded — unless the admin passes `override_weather: true` with a reason (audit-logged).',
      steps: [
        '4 default profiles are seeded on first boot: Student Solo (Day VFR), Dual Training, Night VFR, IFR Training. Each is a typical GACA/FAA conservative baseline.',
        'Edit any profile inline — the Save Changes button appears once a field is dirty.',
        'Add custom profiles for specific operations (e.g. "Cross-country Solo", "Mountain Training") with their own thresholds.',
        'Use the Live METAR check at the top to verify what dispatch will see for a given ICAO + stage combination right now.',
        'Seeded defaults cannot be deleted (only edited). Custom profiles can be deleted, but it is audit-logged.'
      ]
    },
    '/admin/data-tools': {
      title: 'Admin Data Tools',
      overview: 'Surgical maintenance utilities for production data. Every action is audit-logged.',
      steps: [
        'Backfill course attendance — forge course_sessions[].attendance[] rows so the live exam-eligibility gate reflects reality for a legacy cohort.',
        'Pick the course, add cohort rows (academic_number + target %), click Dry-run.',
        'Review the plan table: each row shows the required present/absent counts and the number of Δ actions to reach the target %.',
        'Apply is only enabled after a successful dry-run.',
        'If the course has zero `course_sessions`, fill "Synthesize sessions" (e.g. 10) — the tool will create placeholder sessions first.',
        'Every Apply writes an `audit_logs` row with action=`attendance_backfill` containing the full cohort + sessions touched.',
        'Idempotent — re-running with the same inputs makes no further changes.'
      ]
    },
    '/partner-academies': {
      title: 'Partner Academies',
      overview: 'Manage Sager Aviation\'s partnerships with foreign academies (FAA Part 141 in USA, SACAA Part 141 in South Africa, etc.). Each partner holds a pre-paid credit balance in their local currency; every Sager student flight at the partner debits the credit at the per-flight rate defined in the signed Training Services Agreement.',
      steps: [
        'Click "+ Add academy" to launch a 3-step modal: basic info (name, ISO country dropdown auto-fills currency, contact email) → optional agreement details (rate, credit ceiling, FX override, validity dates) → optional TSA PDF upload.',
        'Each academy card has Edit (pencil) and Delete (trash) icons. Delete is smart: if the academy has no ledger / payments / attributions it is hard-deleted; otherwise it is soft-deleted (status flips to `inactive`) so financial history is preserved.',
        'Toggle "Show inactive" to surface soft-deleted academies. Click Reactivate to flip them back to `active`.',
        'Click Open details to view the 4 tabs: Ledger (deposits / debits / running SAR balance), Agreements (versioned TSAs with PDF upload), Payments (with receipt upload + partner-confirmation flow), and Flight Attributions (3-step state machine).',
        'On the Ledger tab, generate a Monthly Reconciliation Statement (PDF or Excel) for any month — SAR letterhead, transaction list, signature blocks. Partner finance can paste straight into their books.',
        'Live FX is fetched from open.er-api.com (24h cached); each agreement can override the rate to lock it for the contract period.',
        'A low-credit alert tile on Today\'s Operations bar fires when any partner balance drops below its configured `low_credit_threshold_percent` — critical at <5%, attention at threshold.'
      ]
    },
    '/partner-portal': {
      title: 'Partner Portal',
      overview: 'Welcome screen for partner academy users. Shows live credit balance, the current Training Services Agreement, payment history with the option to confirm receipt, flight attribution log, and a weekly schedule request form.',
      steps: [
        'Confirm payment receipts as they appear — Sager Aviation finance is waiting on your acknowledgement before the funds are considered confirmed.',
        'Open and review the active Training Services Agreement PDF at any time.',
        'Submit a weekly flight schedule request — Sager admin reviews and slots into the master scheduler.',
        'All flight attributions and credit-ledger entries are read-only from the portal — they reflect what Sager Aviation\'s system has recorded.',
      ]
    },
    '/alumni': {
      title: 'Alumni Directory',
      overview: 'Post-completion directory of every graduate — tracks BFR, medical, employment, and upcoming renewals. Required for GACA compliance audits.',
      steps: [
        'An alumnus = any student with a "completed" enrollment OR an issued Program Graduation Diploma. Auto-populated, no manual seeding needed.',
        'Filter by employment status, search by name/academic #/employer, or toggle "Expiring certs only" to focus on renewal outreach.',
        'Click any alumnus card to open the profile drawer — contact, post-completion record, and every certificate (active + superseded/renewed).',
        'Admins can Edit the post-completion record: BFR date (biennial flight review, validity 24 months), Medical expiry + class (1/2/3), Employment (status/company/role/start date), LinkedIn URL, Alumni notes. Every edit is audit-logged.',
        'Stats tiles flag medicals expiring within 90 days and BFRs due within 90 days — the two recurring-training trigger points.'
      ],
      byRole: {
        instructor: {
          overview: 'Browse the school\'s graduates — read-only directory with renewal alerts for recurrent-training planning.',
          steps: [
            'Use filters to find alumni by employment status or upcoming renewals.',
            'The profile drawer shows contact info, certificates, and the active/superseded split.',
            'Editing alumni metadata is admin-only.'
          ]
        },
        finance: {
          overview: 'Graduated certificate-holders view — use for billing follow-ups and recurrent-training revenue forecasting.',
          steps: [
            'Filter by employment status and expiring-certs to prioritise outreach.',
            'All alumni fields are read-only for Finance; escalate changes to Admin.'
          ]
        }
      }
    },
    '/licenses': {
      title: 'Authority License Applications',
      overview: 'Track the regulator-facing process that follows a Program Graduation Diploma — GACA, EASA, FAA, or SACAA pilot licenses.',
      steps: [
        'Click "+ New Application" to start — pick the student\'s diploma, authority (GACA / EASA / FAA / SACAA), and license type (PPL / CPL / ATPL / IR / CFI / MEL / MEI). The system refuses to create an application without a valid diploma.',
        'Walk the 7-state pipeline with one click: Draft → Recommendation Issued → Submitted to Authority → Under Review → Approved → Issued. Rejections require a reason and can be re-opened to Draft.',
        'Download the ATO Recommendation Letter PDF (A4, Head of Training signature line). PDFs generated while the application is in Draft or Rejected state are automatically watermarked DRAFT/REJECTED in red diagonal text — you can never accidentally forward an early draft to a regulator.',
        'Marking "Issued" requires the authority\'s license number + issue/expiry dates. That information is stamped back onto the source diploma cert (authority_license_number) so the full training→license audit chain is visible forever.',
        'Students receive a notification on every milestone (recommendation issued, submitted, approved, issued, rejected).'
      ],
      byRole: {
        student: {
          overview: 'Track your license application with the regulator — from school recommendation to official license issue.',
          steps: [
            'You can see only your own applications. Open any to view the timeline and download the recommendation letter.',
            'Status milestones (recommendation, submitted, approved, issued) trigger notifications.',
            'Only staff can create, advance, or reject applications — if you need a new application, contact the training department.'
          ]
        },
        instructor: {
          overview: 'Create and advance license applications for your trainees. Reject with a reason when the regulator declines.',
          steps: [
            'Start from a graduated student\'s diploma. Pick authority + license type.',
            'Advance through the pipeline as paperwork moves forward — the system enforces the correct order.',
            'When the authority issues the license, record the license number + dates exactly as printed.'
          ]
        },
        finance: {
          overview: 'Read-only visibility into the license-fee pipeline — use status filters to forecast issued vs pending applications.',
          steps: [
            'Filter by status / authority to see active applications.',
            'Creation and advancement are admin/instructor only.'
          ]
        }
      }
    },
    '/user-guide': {
      title: 'User Guide',
      overview: 'The complete operational manual for AeroSky Intelligence — every role, every workflow, with enforced gates and tips.',
      steps: [
        'Use the left table of contents (or the search box at the top) to jump to any section.',
        'Sections follow a consistent pattern: Goal, Who can do it, Where to find it, Steps, Gates, Tips.',
        'Click "Print" to generate a printable version of the full guide.',
        'Every open question can also be escalated via Tasks → + New Task → assigned to Admin.'
      ]
    },
    '/leaves': {
      title: 'Leaves & Absences',
      overview: 'Manage employee leave requests with balance tracking and approval workflow.',
      steps: [
        'Employees submit leave requests with type, dates, and reason.',
        'Admin reviews and approves/rejects leave requests.',
        'Track leave balances: annual, sick, exceptional, emergency.',
        'View leave history and calendar.'
      ]
    },
    '/absences': {
      title: 'Absence Tracking',
      overview: 'Track student and employee absences with automated escalation.',
      steps: [
        'Record absences with date, reason, and excused status.',
        'Change your mind later: use "Mark excused" / "Mark unexcused" on any absence or late-arrival row. Excusing an absence waives it in the student\'s conduct record and cancels its unpaid SAR fine invoice.',
        'Automatic escalation: 3 unexcused → warning, 5 → final warning, 7+ → dismissal referral.',
        'View absence statistics and patterns.'
      ]
    },
    '/academic-lifecycle': {
      title: 'Academic Lifecycle',
      overview: 'Manage student academic status changes: withdrawals, counseling, and reinstatement.',
      steps: [
        'Process temporary/permanent withdrawals with reason and dates.',
        'Record academic counseling sessions with recommendations.',
        'Manage reinstatement applications and GACA/SACAA exam applications.',
        'Track student academic status history.'
      ]
    },
    '/contracts': {
      title: 'Contracts & Recruitment',
      overview: 'Saudi Labor Law compliant employment contracts with recruitment pipeline.',
      steps: [
        'Create contracts with Saudi Labor Law fields: GOSI, 90-day probation, housing/transport allowances.',
        'Track contract status: draft, active, expired, terminated.',
        'Manage recruitment: CV uploads, candidate profiles, hiring pipeline.',
        'Auto-promotion workflows based on salary scale.'
      ]
    },
    '/salary-scale': {
      title: 'Role & Salary Scale',
      overview: 'Define salary ranges for organizational roles. Used system-wide for role dropdowns.',
      steps: [
        'Add roles with minimum and maximum salary ranges.',
        'Roles defined here appear in all role dropdowns across the system.',
        'Edit or delete roles as organizational structure changes.'
      ]
    },
    '/disciplinary': {
      title: 'Disciplinary & Complaints',
      overview: 'Submit and manage complaints, warnings, and disciplinary actions.',
      steps: [
        'Anyone can submit complaints (students, staff, instructors).',
        'Non-admin users see only their own submitted complaints.',
        'Admin can investigate, refer to committee, and issue penalties.',
        'Warning escalation: 1st → 2nd → 3rd (final) → dismissal.'
      ],
      byRole: {
        student: {
          overview: 'Submit a complaint or grievance and follow its investigation status. You only see complaints you submitted or that concern you.',
          steps: [
            'Click "+ New Complaint" to file a new grievance with subject, description and evidence.',
            'Track the status of your submission on your complaints list.',
            'Once the committee responds you will be notified — the outcome is recorded here.'
          ]
        },
        instructor: {
          overview: 'Submit complaints and view referrals involving you. Only your own records are visible.',
          steps: [
            'Use "+ New Complaint" to report a student behavior or school issue.',
            'Any disciplinary referral involving you appears on this page.',
            'Contact HR / Admin & Communications for follow-up questions.'
          ]
        }
      }
    },
    '/admin-comms': {
      title: 'Admin & Communications',
      overview: 'Circulars, form templates with fillable fields, generated forms with PDF export, and visa tracking.',
      steps: [
        'Circulars: Create and broadcast announcements by audience (all, students, instructors, etc.).',
        'Form Templates: 48 templates stored in database with fillable field definitions. Admin can add/edit/delete templates.',
        'Generate Form: Select a template, fill in dynamic fields, save as a record. Download as PDF or print.',
        'Visa Tracking: Track student visa applications with document checklist and embassy appointments.',
        'Role-based access: Instructors see 29 templates, Students see 25 templates. Admin sees all 48.'
      ],
      byRole: {
        student: {
          overview: 'Read circulars sent to you and fill out the forms you need (leave requests, declarations, etc.).',
          steps: [
            'Circulars tab: Read school-wide announcements in your inbox.',
            'Forms tab: Pick a template (25 available to students) and fill it in.',
            'Download the completed form as PDF or print for submission.'
          ]
        },
        instructor: {
          overview: 'Access 29 forms available to instructors and read communications from administration.',
          steps: [
            'Circulars tab: School announcements from the administration.',
            'Forms tab: Select a template (29 available to instructors) and fill in.',
            'Export as PDF or print the completed form.'
          ]
        }
      }
    },
    '/reports-dashboard': {
      title: 'Reports Dashboard',
      overview: 'HR and Academic affairs dashboards with key organizational metrics.',
      steps: [
        'HR Dashboard: Leave balances, absence rates, contract expiry, headcount by department.',
        'Academic Dashboard: Student status distribution, study duration, exam pass rates.',
        'Export dashboard data for reporting.'
      ]
    },
    '/compliance-reports': {
      title: 'Compliance Reports',
      overview: 'Regulatory compliance tracking for GACA, EASA, FAA, and SACAA.',
      steps: [
        'View compliance status by authority.',
        'Track required certifications and their validity.',
        'Generate compliance reports for audits.'
      ]
    },
    '/medical-alerts': {
      title: 'Medical Compliance',
      overview: 'Monitor medical certificate expiry for students and staff.',
      steps: [
        'View upcoming medical certificate expirations.',
        'Color-coded urgency: expired, expiring soon, valid.',
        'Send renewal reminders.'
      ]
    },
    '/document-expiry': {
      title: 'Document Expiry',
      overview: 'Track expiring documents across the organization.',
      steps: [
        'View all documents nearing expiry.',
        'Filter by document type and urgency level.',
        'Automated alerts for critical expirations.'
      ]
    },
    '/security': {
      title: 'Security Dashboard',
      overview: 'Monitor system security events and user activity.',
      steps: [
        'View login attempts, failed logins, and suspicious activity.',
        'Track user sessions and access patterns.',
        'Security event logs with filtering and export.'
      ]
    },
    '/documents': {
      title: 'Documents',
      overview: 'Central document management with upload and categorization.',
      steps: [
        'Upload documents with category tags and descriptions.',
        'Search and filter documents by type, owner, and date.',
        'Access control based on user roles.'
      ],
      byRole: {
        instructor: {
          overview: 'Access reference documents shared with instructors and upload documents related to your teaching.',
          steps: [
            'Filter by category to find what you need faster.',
            'Upload a document with a clear title and category tag.',
            'You can only see documents marked for instructors or the whole school.'
          ]
        }
      }
    },
    '/company/documents': {
      title: 'Compliance Documents',
      overview: 'Regulatory compliance document repository.',
      steps: [
        'Upload and manage compliance-related documents.',
        'Track document versions and validity periods.',
        'Organize by regulatory authority.'
      ]
    },
    '/audit-logs': {
      title: 'Audit Logs',
      overview: 'System audit trail for all user actions and data changes.',
      steps: [
        'View chronological log of system events.',
        'Filter by user, action type, and date range.',
        'Export audit logs for compliance reviews.'
      ]
    },
    '/crm': {
      title: 'CRM / Leads',
      overview: 'Lead management and enrollment pipeline for prospective students.',
      steps: [
        'Add leads with source, program interest, and contact details.',
        'Track lead status: new, contacted, qualified, enrolled, lost.',
        'Pipeline visualization and conversion metrics.'
      ]
    },
    '/tasks': {
      title: 'Operations Tasks',
      overview: 'Task management for operational activities and follow-ups.',
      steps: [
        'Create tasks with assignee, priority, and due date.',
        'Track task status: open, in-progress, completed.',
        'Filter by assignee, priority, and status.'
      ]
    },
    '/batches': {
      title: 'Batches',
      overview: 'Group students into batches for cohort-based training management.',
      steps: [
        'Create batches linked to programs and start dates.',
        'Assign students to batches for group tracking.',
        'Monitor batch progress and completion rates.'
      ]
    },
    '/company/settings': {
      title: 'Company Settings',
      overview: 'Configure organization details, logo, contact information, and authorities.',
      steps: [
        'Set company name, legal name, and registration details.',
        'Upload company logo (used in PDF exports and print pages).',
        'Configure compliance authorities: GACA, EASA, FAA, SACAA.',
        'Set contact details: phone, email, address.'
      ]
    },
    '/company/locations': {
      title: 'Locations',
      overview: 'Manage branch offices, training centers, and airport locations.',
      steps: [
        'Add locations with address, coordinates, and type.',
        'Set primary location for official correspondence.',
        'Link locations to operations and scheduling.'
      ]
    },
    '/integrations': {
      title: 'Integrations',
      overview: 'Configure third-party service connections: Stripe, Zoho, SendGrid, Weather API.',
      steps: [
        'Connect payment processing via Stripe.',
        'Link accounting with Zoho.',
        'Configure email notifications via SendGrid.',
        'Set up weather and NOTAM API connections.'
      ]
    },
    '/notifications': {
      title: 'Notifications',
      overview: 'System alerts and notification preferences.',
      steps: [
        'View system notifications and alerts.',
        'Configure notification preferences by type.',
        'Mark notifications as read or dismiss.'
      ]
    },
    '/users': {
      title: 'User Management',
      overview: 'Manage user accounts, roles, and access permissions.',
      steps: [
        'Create user accounts with role assignment.',
        'Reset passwords and manage account status.',
        'View user activity and last login.'
      ]
    },
    '/instructor-portal': {
      title: 'Instructor Portal',
      overview: 'Dedicated workspace for instructors: students, flights, grading, and availability.',
      steps: [
        'Dashboard: Assigned students, pending requests, today\'s sessions, monthly hours.',
        'Flight Requests: View and manage student flight booking requests.',
        'My Students: Quick overview of assigned students and their progress.',
        'My Schedule: Weekly view of scheduled flights and ground sessions.',
        'Log Flight & Lesson Grading: Record flight hours and grade student performance.',
        'Results & Grades → View Recorded: every Exam Result and Skill Test row has a "Request change" button — opens an audit-logged amendment workflow that routes to Chief Instructor / admin for approval.',
        'The Student column in all 3 result tables now shows "Name (#academic_number)" — never raw IDs.'
      ]
    },
    '/accounting': {
      title: 'Accounting Hub (CFO Cockpit)',
      overview: 'Unified GL cockpit: trial balance, balance sheet, cash flow, chart of accounts, journal, credit notes, cost centers, aged payables, financial pack PDF, fiscal-year close, and finance alerts (with FX drift + Snooze).',
      steps: [
        'Use the tab bar to jump between statements. All tabs read from the double-entry general ledger — Trial Balance must always be in balance.',
        'FX Drift widget (top strip): compact snapshot of every locked-rate bill/invoice, with per-doc drift chips. Click a chip to jump straight to the underlying document; click the mail icon to email the party a fresh statement of account.',
        'Credit Notes tab: Issue a new credit note against any invoice with a mandatory ZATCA reason; refund outstanding amounts (bank / cash / card / wallet). Each posting reverses the invoice journal automatically.',
        'Alerts tab: unresolved finance alerts sorted by severity. Snooze noisy ones for 24h (they stay for audit but drop out of the feed + daily SendGrid fanout). Categories include petty_cash_variance, fx_drift, CSID expiry.',
        'Fiscal Close tab: at year-end, close the books to sweep Revenue and Expenses into Retained Earnings.',
        'Financial Pack tab: one-click consolidated Board Pack PDF (P&L + BS + CF + Aged AR/AP) for the selected period — same output that the quarterly CRON emails to the board.',
        'Bank Recon tab → Statement lines: import your bank statement (CSV from any bank — columns are auto-detected and can be re-mapped — or SWIFT MT940). The preview shows duplicates, invalid rows and suggested matches to student payments / expenses (same amount within ±N days); confirm to import. Unmatched lines: "Find match" (±14 days), "Post to GL" or accept a rule suggestion; "Unlink" reverses a match.',
        'Bank Recon tab → Rules: e.g. "FUEL" (withdrawals) → 5100 Fuel, optional cost center and 15% VAT split. Rules only SUGGEST an account — finance confirms each posting; the journal preview shows the exact DR/CR lines before posting.',
        'Bank Recon tab → Reconciliation report: pick bank account + month, enter the statement closing balance → ledger vs bank, deposits in transit, outstanding payments, bank-only items, adjusted balance and difference. Download the PDF or "Sign off & save snapshot" for the auditor.',
        'Month-End Close tab: one checklist per month — auto-detected steps (bank sign-offs, unmatched lines, depreciation, deferred revenue, vendor bills, petty cash, WHT, VAT at quarter-end, fiscal close in December) plus manual sign-offs with notes. "Go fix" jumps to the right page. Admin can "Lock month" at 100% (unlock needs a reason). Finance and admins receive a reminder on the 3rd and 7th working day while steps are pending; "Send reminder now" is also available.'
      ]
    },
    '/violations': {
      title: 'Student Violations & Conduct Analytics',
      overview: 'Record dress-code, lateness, absence and conduct violations; the student sees them instantly in the portal. Conduct Analytics shows where violations are rising so the registrar can act early.',
      steps: [
        'Violations tab: "Record violation" → pick student, type (configurable), severity/points, date and description. The student gets a notification and must acknowledge it in Portal → Conduct. Admins resolve / dismiss / delete.',
        'Escalation: when a student crosses a points threshold (Escalation panel → settings) a warning letter PDF or committee referral is generated automatically and emailed (when SendGrid is configured).',
        'Conduct Analytics tab: heatmap of incidents per week (recorded violations + unexcused absences + late arrivals) grouped by class, batch or instructor. Darker = more incidents. "Last 4w" compares with the previous 4 weeks; a red triangle = Early warning (rising ≥50% and ≥3 incidents).',
        'Click a cell to list the incidents of that week; click a row name for the type breakdown. Top risers lists groups trending up.',
        'Log intervention: on a selected row click "Log intervention" (class talk, instructor meeting, parent contact, schedule change, counselling…). A green dot marks the week; the outcome (Effective / No change / Worse) is computed automatically from incidents 4 weeks after vs before.',
        'Suggest next step: the playbook ranks intervention types from your own outcome history, the dominant violation type and what was already tried; "Use" pre-fills the form. "Explain with AI" adds a short written rationale.',
        'Weekly digest: every Monday 06:00 admins (and optionally instructors of flagged classes) receive a summary of rising classes, new early warnings and open interventions. Preview or "Send now" from the panel.'
      ],
      byRole: { instructor: { overview: 'You can record violations for your students and view the heatmap, interventions and digest preview. Thresholds, digest settings and deletions are admin-only.' } }
    },
    '/batch-timetable': {
      title: 'Batch Timetables',
      overview: 'Weekly recurring timetable per batch (cohort). Generating a timetable creates the classes and per-day sessions automatically; students see it in their portal.',
      steps: [
        'Pick the batch, then add slots on the week grid (day, time, course, instructor, location). Instructor conflicts are skipped and reported.',
        '"Generate" creates one class per slot and a session for every date in the term; re-generating is safe (idempotent) and removed slots cancel their future sessions.',
        'Roster sync: students added to or removed from the batch are enrolled / removed from the timetable classes automatically.',
        'Archive a timetable when the term ends; the Sessions list shows the generated calendar.'
      ]
    },

    '/debrief': {
      title: 'Lesson Debrief (Electronic Training Record)',
      overview: 'Tablet-first grading at the aircraft. One debrief writes the lesson grade, the flight log and a signed training record in a single step.',
      steps: [
        'Pick the student — the system shows the next syllabus lesson that is not yet Satisfactory (stage, lesson number, objective, completion standard).',
        'Grade every maneuver S (Satisfactory) or U (Unsatisfactory) with the large buttons. Any U forces the lesson result to Unsatisfactory and requires a remedial plan.',
        'Enter flight time, aircraft and landings (flight/simulator lessons) or ground time (ground lessons), plus your remarks.',
        'Sign: draw your signature or enter your 4-digit signing PIN (set it under Training Records → Set signing PIN).',
        'Submit → lesson grade saved, flight log created and linked, student notified to countersign. The record seals (SHA-256 hash chain) once the student signs.',
        'Offline at the aircraft: the page keeps working — students, aircraft and the next lesson are cached; a submitted debrief is stored on the device and synced automatically when the connection returns ("Sync now" is also available). Server rejections (e.g. wrong PIN) are listed in the banner for correction.',
        'Currency gate: if your medical or license has expired the page shows a red banner and submission is blocked; approaching expiries and recency lapses show as warnings.',
        'Student countersigns now: tick "Student countersigns now on this device", hand over the tablet — the student draws (you witness) or enters their own signing PIN. Both signatures travel in the same submission, so the record is sealed immediately, online or offline.'
      ],
      byRole: { instructor: { overview: 'Only students assigned to you are listed. Grade, sign, hand the tablet to the student to review later from their portal.' } }
    },
    '/training-records': {
      title: 'Training Records',
      overview: 'Chronological electronic training records per student with both signatures, integrity seal, PDF export and seal verification.',
      steps: [
        'Filter by student and status (Awaiting student / Sealed). Click a row to open the full record with maneuver grades and signatures.',
        'PDF: printable grade sheet with embedded signatures and the integrity hash — hand it to a GACA/EASA inspector as-is.',
        'Verify seal: recomputes the SHA-256 hash and confirms the record has not been altered since the student signed.',
        'Set signing PIN: a 4-digit PIN lets you sign without drawing. PIN signatures are audit-logged under your name.'
      ],
      byRole: {
        student: {
          overview: 'Your official training record. Records your instructor has graded appear here for you to review and countersign.',
          steps: [
            'The amber banner shows how many records await your signature — click "Review & sign".',
            'Read the maneuver grades and remarks. Add an optional comment if you disagree, then draw your signature or use your PIN.',
            'Once signed the record is sealed and can no longer change. Download the PDF for your personal logbook file.'
          ]
        }
      }
    },
    '/auto-scheduler': {
      title: 'Auto-Scheduler',
      overview: 'Generates a complete training week: every ready student gets their next flight lesson matched to an available instructor, a serviceable aircraft and the duty limits.',
      steps: [
        'Pick the week start and a strategy — Continuity (primary instructor + same aircraft type), Utilization (fill every airframe) or Balanced (even instructor load).',
        'Duty limits (admin): instructor max hours/sessions per day, student flights per day/week, block length, turnaround, day window and working days.',
        'Propose week → a DRAFT appears grouped by day with the reason each match was made. Skipped students are listed with the exact blocker (medical, financial hold, no enrollment, no slot).',
        'Edit any slot (time, instructor, aircraft) or remove it. Then Publish → bookings are created and students notified; slots that now conflict are reported, not overwritten.',
        'Students who are medically grounded, delinquent or whose medical has expired are never scheduled.',
        'Instructor currency gate: instructors with an expired medical or license are excluded automatically (listed under "Instructors excluded"). Recency (default 90 days) and missing type rating are warnings unless you tick "blocks" in Duty limits.',
        'Weather gate: "Weather check" evaluates every slot against the stage minima profile (Solo / Dual / Night / IFR) using the TAF for the base airport. Publish holds any slot below minima; a chief instructor can override with a mandatory, audit-logged reason. The chip top-right shows "Live TAF" when a CheckWX key is saved under Integrations, otherwise "Simulated forecast" (·sim).',
        'Currency panel: lists every instructor AND student medical/license expiring within 30 days or already expired. Reminders are sent automatically every day at 06:30 UTC (30 / 14 / 7 / 0 days); "Send due reminders now" forces a run. Students with an expired medical or student pilot license are skipped by the scheduler with the reason shown.',
        'Pre-flight weather recheck: every day at 14:00 UTC (17:00 Riyadh) the TAF gate re-runs for tomorrow\'s bookings at every base. Slots that drop below minima are flagged on the booking and dispatch (admins), the instructor and the student get a critical alert — once per booking. "Run for tomorrow now" forces it.',
        'Multi-base fleets: set each aircraft\'s Base location (ICAO) on the Aircraft page — a picker suggests Saudi, USA and South Africa training airports. The weather gate pulls the TAF for that station; aircraft without a base use the default in Duty limits.'
      ]
    },
    '/utilization': {
      title: 'Utilization Analytics',
      overview: 'Where the money is made or lost: hours per airframe, instructor utilization, cancellation causes and student pace for any period.',
      steps: [
        'Choose a date range or a 7/30/90-day preset.',
        'Hours per airframe: grey bars are idle aircraft, amber are in maintenance. Hrs/aircraft/week is the headline fleet KPI.',
        'Instructor utilization: flown hours divided by hours of declared availability. Instructors without availability show "no availability set".',
        'Heatmap: hours by weekday per tail — dark cells are idle capacity you can sell or schedule into.',
        'Student pace: hours per week with On pace / Slow / At risk flags so training managers can intervene early.'
      ]
    },
    '/operations-board': {
      title: 'Operations Board (Multi-Base)',
      overview: 'One screen per training base — Riyadh, USA, South Africa or any ICAO — with serviceable fleet, active instructors, today\'s bookings, 30-day hours and the live METAR judged against dual-training minima.',
      steps: [
        'Bases are derived from each aircraft\'s Base location (ICAO). Aircraft without one roll up under the default base from Auto-Scheduler → Duty limits (starred card).',
        'Weather strip: flight category (VFR/MVFR/IFR/LIFR), GO / below-minima verdict for Dual Day training, wind, visibility, temperature and the raw METAR. Requires the CheckWX key under Integrations.',
        'Instructors listed are those with bookings on that base\'s aircraft in the last 30 days — a practical view of who actually flies there.',
        'Use "Schedule" to jump to the calendar and "Full weather" for METAR/TAF/NOTAM detail. Refresh pulls fresh METARs.'
      ]
    },
    '/finance-home': {
      title: 'Finance Home',
      overview: 'Landing view for the finance team: cash position, AR aging, and vendor bills due this week — all reconciled to the same ledger the detailed reports use.',
      steps: [
        'Cash Position (green): live balances of every cash/bank account in the chart of accounts, with the net 30-day change.',
        'AR Aging (blue): outstanding student invoices by age bucket (0-30 / 31-60 / 61-90 / 90+) plus total tuition balances. Click "Report" for the full Aged Receivables report.',
        'AP Due This Week (amber): vendor bills due within 7 days, overdue bills, bills due later, and how many still await approval. Click "Vendor Bills" to action them.',
        'The two lists below rank the most overdue student invoices (jump to Invoice Dunning) and the bills to pay next.',
        'Quick links at the bottom open the most-used finance modules — including Month-End Close (the monthly checklist with bank sign-off, VAT and fiscal close). Use "Refresh" to re-pull live numbers.',
        'Tip — the Finance & Accounting sidebar is grouped (Overview / Receivables / Payables / General Ledger / Tax & Zakat / Reports). Click a group heading to fold or unfold it; your choice is remembered on this device.'
      ]
    },
    '/petty-cash': {
      title: 'Petty Cash',
      overview: 'Physical cash-box tracking with double-entry postings and auditor-grade reconciliation.',
      steps: [
        'Create a box: name, location, custodian, float, currency. Balance auto-updates on every top-up/expense/reconcile.',
        'Top-up (DR petty cash · CR main cash 1010) and Expense (DR expense · CR petty cash) — the ledger stays in balance automatically.',
        'Reconcile: enter physical count → variance journal posts to Cash Short/Over. Any single-shot OR cumulative variance ≥ SAR 500 raises a de-duplicated finance_alert (severity escalates to CRITICAL at 2× threshold).',
        'Print Variance Report (top-right): opens an auditor-ready printable summary with every box, full reconciliation history, shortage/overage counts, and signature blocks. Uses @page margins for clean paper output.'
      ]
    },
    '/opening-balances': {
      title: 'Opening Balances Wizard',
      overview: 'Load prior-period closing balances into the new fiscal year with double-entry safeguards.',
      steps: [
        'Step 1 — Pick the as-of date (usually the last day of the previous fiscal year).',
        'Step 2 — Provide data: type rows manually, paste CSV, or click "Upload filled template" to load a .csv. "Download CSV Template" hands back a BOM-prefixed CSV pre-filled with every active chart-of-accounts row.',
        'Step 3 — Review + Diff Preview: click "Preview diff vs current balances" to see per-account current → new balance, with amber warnings on any account that already has data. Auditors can hit "Export diff CSV" or "Copy signed link (24h)" — the signed link is a portal-less URL any external reviewer can open.',
        'Step 4 — Post: the OBE plug (3900 Opening Balance Equity) auto-balances the journal. Every import is reversible via /opening-balances/reset (admin only).'
      ]
    },
    '/vendor-bills': {
      title: 'Vendor Bills (AP with 3-way match)',
      overview: 'Formal accounts-payable workflow: PO ↔ GRN ↔ Bill, VAT input tracking, multi-currency, and attached invoice preview.',
      steps: [
        'New Bill: pick vendor + line items, VAT, PO/GRN. Currency dropdown supports SAR / USD / EUR / GBP / ZAR — a live-FX preview strip shows "1 USD = 3.75 SAR" and the SAR equivalent of the total.',
        'Lock FX rate (contract): tick "Lock a specific FX rate" for long-term vendor agreements; enter the contracted rate + optional "valid until" date. If today is past the validity window, the override auto-falls-back to live and the bill is stamped `fx_override_expired`.',
        '3-way match: run "Match" to validate quantity/price vs PO + GRN. Approve → Mark Paid triggers the SAR-denominated journal (DR expense · CR cash · optional VAT input) — memos carry the origin currency, locked rate, and source for audit.',
        'Attach the vendor\'s original PDF or image invoice (20 MB max, PDF/PNG/JPG). The row shows a mini thumbnail — images render inline, PDFs render page-1 via the native browser viewer. Click the thumb to open the full attachment.',
        'When the FX Drift widget flags this bill, an alert appears; the click-through from the widget scrolls the row into view and flashes it amber.'
      ]
    },
    '/wht': {
      title: 'Withholding Tax (WHT)',
      overview: 'Saudi WHT module — 5% (services) / 15% (royalties, dividends) brackets with certificate generation and journal posting.',
      steps: [
        'Log a payment: pick vendor, gross amount, tax rate (5% or 15%). Net amount + tax amount compute automatically.',
        'Submit → journal posts DR expense · CR bank · CR WHT payable. WHT certificate PDF generates for the vendor.',
        'Track filing status: Pending → Filed with ZATCA → Paid.'
      ]
    },
    '/zakat': {
      title: 'Zakat Calculator',
      overview: 'Zakat base + 2.5% assessment for Saudi companies (GAZT/ZATCA compliant).',
      steps: [
        'Enter the Zakat base components (equity, reserves, provisions, less deductions).',
        'Zakat auto-computes at 2.5% of the qualifying base.',
        'Download the Zakat return form PDF for ZATCA filing.'
      ]
    },

  },
  ar: {
    '/': { title: 'لوحة التحكم التشغيلية', overview: 'المركز الرئيسي لمراقبة جميع عمليات التدريب — الامتثال، التجديدات، والشؤون التأديبية.', steps: ['عرض المقاييس الرئيسية: الطلاب، الموظفون، الطائرات، وحالة الامتثال.', 'بطاقة الامتثال الطبي تعرض الشهادات المنتهية بترميز لوني حسب الأولوية.', 'بطاقة تجديد الشهادات الجديدة (تختفي تلقائياً إذا فارغة): تعرض كل شهادة ضمن نافذة 90 يوماً للتجديد مجمّعة حسب الحالة (منتهية / 30ي / 60ي / 90ي). للمسؤولين زر "تجديد" بنقرة واحدة — ينشئ شهادة جديدة في السلسلة مع سجل تدقيق.', 'بطاقة الجدول الزمني التأديبي تعرض أحدث نشاط للشكاوى.', 'روابط سريعة للوحدات المستخدمة بكثرة.'] },
    '/students': {
      title: 'إدارة الطلاب',
      overview: 'إدارة الطلاب والشهادات الطبية والوثائق والتسجيل في البرامج وتقدم المقررات والحصص والامتثال وأهلية الشهادات.',
      steps: [
        'انقر "تسجيل طالب" لإنشاء طالب جديد بالمعلومات الشخصية والشهادة الطبية والترخيص.',
        'تبويبات الملف الشخصي: الهوية، الاتصال، الطبي، الرخص، خبرة الطيران، اللغة، التسجيل، تقدم المقررات.',
        'تبويب التسجيل: التسجيل في برنامج — النظام يسجل تلقائياً في جميع المقررات المطلوبة. قائمة التحقق الجديدة لأهلية الشهادة تعرض دورة حياة من 7 مراحل (لم يبدأ → قيد التدريب → اجتياز الامتحانات → جاهز للاختبار العملي → اجتاز الاختبار العملي → متخرج → تم إصدار الشهادة) مع تحقق لكل متطلب: الساعات الأرضية، ساعات الطيران، الامتحانات، الاختبار العملي، الحضور، التخليص المالي.',
        'شبكة شهادات المقررات تعرض أي مقررات تمتلك شهاداتها — زر "إصدار دبلوم البرنامج" يتاح فقط عند اكتمال جميع المقررات الإلزامية ومرور جميع البوابات.',
      ],
      byRole: {
        instructor: {
          overview: 'تصفح الطلاب المعيّنين لك واطلع على ملفاتهم وتقدمهم وخبرتهم في الطيران.',
          steps: [
            'استخدم البحث والفلاتر لإيجاد طالب من طلابك.',
            'انقر على صف الطالب لفتح ملفه — التبويبات للقراءة فقط للمعلمين.',
            'استخدم تبويبات "خبرة الطيران" و"تقدم المقررات" لمراجعة تاريخ التدريب قبل الدرس.'
          ]
        }
      }
    },
    '/courses': {
      title: 'المقررات',
      overview: 'إدارة المقررات مع حد أدنى للحضور ومعادلات الدرجات المرجحة وتتبع تقدم الطلاب.',
      steps: [
        'انقر "+ إضافة مقرر" للإنشاء بالعنوان والرمز والهيئة والساعات والمنهج.',
        'إعداد "سياسة الحضور والدرجات": الحد الأدنى للحضور وأوزان الدرجات.',
        'الطلاب دون الحد الأدنى للحضور محظورون من الاختبارات.'
      ],
      byRole: {
        student: {
          overview: 'تصفح المقررات المسجل فيها وتحقق من حضورك ودرجاتك وتقدمك في المنهج.',
          steps: [
            'استخدم البحث للعثور على مقرر معين تدرسه.',
            'افتح المقرر لعرض المنهج والوحدات ونسبة حضورك.',
            'إذا كان حضورك أقل من الحد الأدنى ستُحظر من الاختبار النهائي — تواصل مع معلمك.'
          ]
        }
      }
    },
    '/classes': {
      title: 'الحصص والجدول',
      overview: 'إدارة جلسات الحصص كمجموعات مع التسجيل الجماعي وتسجيل الحضور.',
      steps: [
        'التبديل بين العرض الشبكي والتقويمي.',
        'إنشاء حصة: اختر المقرر، البرنامج، المعلم، التاريخ، وقائمة الطلاب.',
        '"تسجيل الكل" يسجل جميع الطلاب في البرنامج والمقررات المطلوبة.'
      ],
      byRole: {
        student: {
          overview: 'اعرض الحصص النظرية المجدولة لك.',
          steps: [
            'استخدم عرض التقويم (أسبوع/شهر) لمشاهدة حصصك القادمة.',
            'انقر على أي حصة لعرض التفاصيل: المقرر، المعلم، الموقع والوقت.',
            'إذا احتجت للاعتذار قدّم طلب غياب من بوابة الطالب.'
          ]
        }
      }
    },
    '/exams': { title: 'الاختبارات', overview: 'إدارة الاختبارات مع نتائج جماعية وبوابة أهلية الحضور.', steps: ['إنشاء اختبارات مرتبطة بمقرر.', 'إدخال الدرجات مباشرة مع حساب تلقائي للنجاح والرسوب.'] },
    '/programs': { title: 'البرامج التدريبية', overview: 'برامج متوافقة مع GACA Part-141 مع ربط المقررات، توفيق الساعات، وبوابات إكمال الشهادة.', steps: ['إنشاء برنامج بالرمز والفئة والهيئة والمدة والرسوم.', 'ربط المقررات المطلوبة في مصفوفة المقررات — التسجيل التلقائي عند الانضمام.', 'لوحة بوابات الإكمال الجديدة: حدد الحد الأدنى لنسبة الحضور (80٪ نموذجي لـ GACA)، مطلوب اختبار مهارة، مطلوب اختبار عملي — هذه تقود محرك أهلية الشهادة.'] },
    '/scheduling': {
      title: 'الجدولة',
      overview: 'عروض تقويمية لجدول الرحلات وتوفر المعلمين وجدول الحصص.',
      steps: [
        'التبويبات: الجدول، التوفر، الحصص.',
        'تبويب الحصص يعرض حصصاً مرمزة بالألوان.'
      ],
      byRole: {
        student: {
          overview: 'اعرض جدولك الشخصي للتدريب — الرحلات والحصص النظرية جنباً إلى جنب.',
          steps: [
            'بدّل بين التبويبات لمشاهدة جدول الرحلات أو الحصص النظرية.',
            'انقر على أي إدخال للحصول على تفاصيل كاملة: الوقت، المعلم، الموقع.',
            'لا يمكنك إعادة الجدولة — تواصل مع معلمك لطلب أي تغيير.'
          ]
        },
        instructor: {
          overview: 'تقويم رحلاتك وحصصك النظرية. حدّث توفرك وراجع الجلسات المعيّنة لك.',
          steps: [
            'استخدم تبويب التوفر لحجب الأوقات غير المتاحة لك.',
            'تبويب الجدول يعرض جميع الرحلات المعيّنة لك.',
            'تبويب الحصص يعرض الجلسات النظرية التي تدرّسها.'
          ]
        }
      }
    },
    '/my-portal': { title: 'بوابة الطالب', overview: 'لوحة شخصية تعرض تقدم التدريب والجدول والأمور المالية.', steps: ['تبويب النظرة العامة: قائمة التهيئة والتسجيل والملخص المالي.', 'تبويبات التدريب والفواتير والوثائق والاختبارات.'] },
    '/income-statement': { title: 'قائمة الدخل', overview: 'قائمة الدخل: الإيرادات، تكاليف التشغيل، إجمالي الربح، المصروفات التشغيلية، صافي الدخل.', steps: ['اختر الفترة: شهرية، ربع سنوية، أو نطاق مخصص.', 'الإيرادات من الفواتير والمصروفات من سجلات المصروفات المعتمدة.', 'إضافة قيود مخصصة: إيرادات أو تكاليف تشغيل أو مصروفات تشغيلية.', 'تحميل PDF أو طباعة بشعار الشركة.'] },
    '/departments': { title: 'الأقسام', overview: 'إدارة الأقسام التنظيمية مع تعيين رؤساء الأقسام وتتبع الموظفين.', steps: ['إضافة قسم جديد بالاسم والوصف والرئيس والحالة.', 'تعديل الأقسام الحالية أو حذفها.', 'عرض عدد الموظفين في كل قسم.'] },
    '/grade-change-requests': { title: 'طلبات تعديل الدرجات', overview: 'سير عمل اعتماد تعديلات الدرجات (المستوى 3 / 4) لسجلات الطيران ونتائج الامتحانات واختبارات المهارات. المسؤولون ورؤساء التدريب يوافقون، والمعلمون يقدّمون الطلبات، وكل إجراء يُسجَّل في سجل التدقيق.', steps: ['زر "+ طلب جديد" في رأس الصفحة (للمسؤول / المعلم) يفتح نافذة الاختيار: اختر نوع السجل (نتيجة امتحان / اختبار مهارة / سجل طيران)، ثم اختر السجل المحدد، ثم اقترح القيم المصححة وفئة السبب والمبرر (10 أحرف على الأقل).', 'يمكن أيضاً إنشاء طلب من زر "طلب تعديل" بمستوى الصف في بوابة المعلم → النتائج والدرجات → عرض المسجّل، أو في /exams → تبويب النتائج، أو في سجلات الطيران (الصفوف المُقيَّمة فقط).', 'عمود الطالب يعرض الآن اسم الطالب ورقمه الأكاديمي لكل طلب.', 'انقر موافقة لتطبيق التغيير عبر سجل grade_history باسمك كموافق. انقر رفض مع ملاحظات إلزامية تظهر للمُقدِّم.', 'انقر تعديل على صف معلّق لتعديل الاقتراح قبل الموافقة (مُسجَّل في التدقيق). لا يمكن للمقدّم تعديل طلبه.', 'زرّا "صندوق الموافقات / طلباتي" للمسؤولين فقط للتبديل بين العرضين. غير الموافقين يصلون مباشرة إلى طلباتي.', 'لا يمكن الموافقة على طلبك الخاص حتى بصلاحية المسؤول (فصل الواجبات).', 'الطالب المتأثر يستلم إشعاراً داخل التطبيق فور تطبيق الطلب.', 'شارة الشريط الجانبي تعرض عدد الطلبات المعلّقة للموافقين (تستثني طلباتك). تحدّث كل 60 ثانية.'] },
    '/invoice-dunning': { title: 'تذكيرات الفواتير المتأخرة', overview: 'تذكيرات آلية للفواتير المتأخرة عند 7 و14 و30 يوماً. كل مرحلة تُنشئ إشعاراً داخلياً وبريداً عبر SendGrid (إن تم إعداده). لا يتم تكرار نفس المرحلة لنفس الفاتورة.', steps: ['ثلاث بطاقات إحصائية: إجمالي الفواتير المتأخرة، الإرسال المعلق، مجموع المبالغ.', 'الجدول يعرض الفاتورة، الطالب، الأيام المتأخرة، المرحلة الحالية، والمراحل التي تم إرسالها.', 'زر "تشغيل المسح الآن" لتشغيل المسح يدوياً بدلاً من انتظار الجدول اليومي 04:30 UTC.', 'فواتير غرامات الغياب مستثناة — لها ماسح خاص بها بمنطق رسوم التأخير.'] },
    '/weather-minima': { title: 'حدود الأرصاد للإقلاع', overview: 'حدود الأرصاد الخاصة بكل مرحلة تدريبية تُستخدم لحجب الإقلاع تلقائياً عند تجاوز METAR للحد. يمكن للأدمن تجاوز الحجب بسبب موثق.', steps: ['4 ملفات افتراضية: طالب فردي نهاراً، تدريب ثنائي، VFR ليلاً، تدريب IFR.', 'تعديل أي ملف مباشرة — يظهر زر الحفظ عند التغيير.', 'إنشاء ملفات مخصصة لعمليات معينة.', 'استخدم فحص METAR المباشر للتحقق من حالة مطار قبل الإقلاع.'] },
    '/user-guide': { title: 'دليل المستخدم', overview: 'الدليل التشغيلي الكامل لنظام أيروسكاي — لكل دور ولكل سير عمل، مع القيود المفروضة تلقائياً والنصائح المفيدة.', steps: ['استخدم جدول المحتويات على اليسار أو مربع البحث أعلى الصفحة للانتقال إلى أي قسم.', 'تتبع الأقسام نمطاً موحداً: الهدف · من يقوم بالمهمة · الموقع · الخطوات · القيود · النصائح.', 'انقر على "طباعة" لتوليد نسخة قابلة للطباعة من الدليل كاملاً.', 'يمكن تصعيد أي استفسار عبر المهام ← + مهمة جديدة ← تعيين للمسؤول.'] },
    '/leaves': { title: 'الإجازات والغياب', overview: 'إدارة طلبات الإجازة مع تتبع الرصيد وسير عمل الموافقة.', steps: ['تقديم طلبات إجازة بالنوع والتواريخ والسبب.', 'مراجعة الطلبات والموافقة أو الرفض.', 'تتبع أرصدة الإجازات: سنوية، مرضية، استثنائية، طارئة.'] },
    '/absences': { title: 'تتبع الغياب', overview: 'تتبع غياب الطلاب والموظفين مع التصعيد التلقائي.', steps: ['تسجيل الغياب بالتاريخ والسبب وحالة العذر.', 'يمكنك لاحقاً الضغط على "Mark excused" لتحويل الغياب من غير مبرر إلى مبرر (يُلغى معه إشعار الغرامة غير المدفوع) أو العكس.', 'تصعيد تلقائي: 3 غير مبررة = إنذار، 5 = إنذار نهائي.'] },
    '/academic-lifecycle': { title: 'دورة الحياة الأكاديمية', overview: 'إدارة تغييرات الحالة الأكاديمية: الانسحاب والإرشاد وإعادة القيد.', steps: ['معالجة طلبات الانسحاب المؤقت والدائم.', 'تسجيل جلسات الإرشاد الأكاديمي.', 'إدارة طلبات إعادة القيد.'] },
    '/contracts': { title: 'العقود والتوظيف', overview: 'عقود عمل متوافقة مع نظام العمل السعودي مع خط التوظيف.', steps: ['إنشاء عقود بحقول نظام العمل: التأمينات، فترة التجربة، بدلات السكن والنقل.', 'تتبع حالة العقود: مسودة، نشط، منتهي.', 'إدارة التوظيف: السير الذاتية وملفات المرشحين.'] },
    '/salary-scale': { title: 'سلم الرواتب', overview: 'تحديد نطاقات الرواتب للأدوار التنظيمية.', steps: ['إضافة أدوار بالحد الأدنى والأقصى للراتب.', 'الأدوار المحددة هنا تظهر في جميع قوائم الأدوار.'] },
    '/disciplinary': {
      title: 'التأديب والشكاوى',
      overview: 'تقديم وإدارة الشكاوى والإنذارات والإجراءات التأديبية.',
      steps: [
        'يمكن لأي شخص تقديم شكوى.',
        'المسؤول يمكنه التحقيق وإحالة وإصدار عقوبات.'
      ],
      byRole: {
        student: {
          overview: 'قدم شكواك وتابع حالة التحقيق. ترى فقط الشكاوى التي قدمتها أو تخصك.',
          steps: [
            'انقر "+ شكوى جديدة" لتقديم شكوى بالموضوع والوصف والمرفقات.',
            'تابع حالة شكواك من قائمة الشكاوى.',
            'ستُبلّغ عند صدور قرار اللجنة — النتيجة مسجلة هنا.'
          ]
        },
        instructor: {
          overview: 'قدم الشكاوى واعرض الإحالات التي تخصك. ترى سجلاتك فقط.',
          steps: [
            'استخدم "+ شكوى جديدة" للإبلاغ عن سلوك طالب أو مشكلة في المدرسة.',
            'أي إحالة تأديبية تخصك تظهر هنا.',
            'تواصل مع الموارد البشرية للاستفسارات.'
          ]
        }
      }
    },
    '/admin-comms': {
      title: 'الإدارة والاتصالات',
      overview: 'التعاميم ونماذج النماذج مع حقول قابلة للتعبئة وتصدير PDF وتتبع التأشيرات.',
      steps: [
        'التعاميم: إنشاء وبث إعلانات حسب الجمهور.',
        'نماذج النماذج: 48 نموذج مع تعريفات حقول. إضافة/تعديل/حذف.',
        'إنشاء نموذج: اختيار قالب وتعبئة الحقول وتحميل PDF.',
        'تتبع التأشيرات: تتبع طلبات تأشيرات الطلاب.'
      ],
      byRole: {
        student: {
          overview: 'اقرأ التعاميم المرسلة إليك واملأ النماذج التي تحتاجها (طلبات إجازة، إقرارات ...).',
          steps: [
            'تبويب التعاميم: اقرأ إعلانات المدرسة في بريدك.',
            'تبويب النماذج: اختر قالباً (25 متاحة للطلاب) واملأ البيانات.',
            'حمّل النموذج المكتمل كـ PDF أو اطبعه للتسليم.'
          ]
        },
        instructor: {
          overview: 'اطلع على 29 نموذجاً متاحة للمعلمين وعلى التواصلات الإدارية.',
          steps: [
            'تبويب التعاميم: إعلانات الإدارة.',
            'تبويب النماذج: اختر قالباً (29 متاحة للمعلمين) واملأه.',
            'صدّر النموذج كـ PDF أو اطبعه.'
          ]
        }
      }
    },
    '/reports-dashboard': { title: 'لوحة التقارير', overview: 'لوحات HR والشؤون الأكاديمية مع مقاييس تنظيمية رئيسية.', steps: ['لوحة HR: أرصدة الإجازات ومعدلات الغياب وانتهاء العقود.', 'لوحة أكاديمية: توزيع حالة الطلاب ومدة الدراسة ومعدلات النجاح.'] },
    '/staff': { title: 'دليل الموظفين', overview: 'إدارة جميع الموظفين بما في ذلك المعلمين والإداريين والماليين.', steps: ['إضافة موظف جديد بالدور والقسم ومعلومات الاتصال.', 'عرض وتعديل ملفات الموظفين.'] },
    '/company/settings': { title: 'إعدادات الشركة', overview: 'تكوين تفاصيل المنظمة والشعار ومعلومات الاتصال.', steps: ['تعيين اسم الشركة والاسم القانوني وتفاصيل التسجيل.', 'رفع شعار الشركة المستخدم في تصدير PDF والطباعة.', 'تكوين هيئات الامتثال والاتصال.'] },
    '/instructor-portal': { title: 'بوابة المعلمين', overview: 'مساحة عمل مخصصة للمعلمين: الطلاب والرحلات والتقييم والتوفر.', steps: ['لوحة المعلومات: الطلاب المعينون والطلبات المعلقة وجلسات اليوم.', 'طلبات الطيران وجدول الرحلات وتقييم الدروس.', 'النتائج والدرجات → عرض المسجّل: كل صف نتيجة امتحان أو اختبار مهارة يحتوي على زر "طلب تعديل" — يفتح سير عمل تعديل الدرجات المُسجَّل في التدقيق.', 'عمود الطالب يعرض الآن "الاسم (#الرقم الأكاديمي)" بدلاً من المعرفات الخام.'] },
    '/partner-academies': { title: 'الأكاديميات الشريكة', overview: 'إدارة شراكات Sager Aviation مع الأكاديميات الأجنبية (FAA Part 141 في الولايات المتحدة، SACAA في جنوب أفريقيا، إلخ). يحتفظ كل شريك برصيد ائتماني مُسبق الدفع بعملته المحلية.', steps: ['زر "+ إضافة أكاديمية" يفتح نافذة من 3 خطوات: المعلومات الأساسية → تفاصيل الاتفاقية → رفع TSA PDF.', 'كل بطاقة أكاديمية لها أيقونات تعديل وحذف. الحذف ذكي: حذف نهائي إذا لم تكن هناك معاملات، حذف مرن (status=inactive) إذا كان للأكاديمية سجل مالي.', 'مفتاح "إظهار غير النشطة" يكشف الأكاديميات المحذوفة مرناً مع زر إعادة التفعيل.', 'انقر "فتح التفاصيل" لعرض 4 تبويبات: السجل المالي، الاتفاقيات، المدفوعات، إسناد الرحلات.', 'في تبويب السجل: أنشئ كشف تسوية شهرية (PDF / Excel) لأي شهر مع شعار وقائمة معاملات وكتل توقيع.', 'سعر الصرف الحي يُجلب من open.er-api.com (مخزّن 24 ساعة)؛ كل اتفاقية يمكن أن تثبت سعراً مخصصاً.', 'بطاقة التنبيه على شريط عمليات اليوم تنطلق عند انخفاض رصيد أي شريك دون عتبة التنبيه المُحدّدة.'] },
    '/partner-portal': { title: 'بوابة الشريك', overview: 'شاشة ترحيب لمستخدمي الأكاديميات الشريكة — رصيد حي، اتفاقية حالية، سجل مدفوعات، إسناد رحلات، طلب جدول أسبوعي.', steps: ['أكّد إيصالات المدفوعات حال ظهورها — قسم المالية ينتظر إقراركم.', 'اقرأ اتفاقية TSA النشطة في أي وقت.', 'قدّم طلب جدول أسبوعي للرحلات.', 'سجلات الرحلات والائتمان للقراءة فقط من البوابة.'] },
    '/weather': {
      title: 'الطقس والإشعارات',
      overview: 'بيانات الطقس الفورية (METAR/TAF) وإشعارات الطيارين لتخطيط الرحلات.',
      steps: [
        'عرض METAR و TAF الحالي للمطارات المكوّنة.',
        'تحقق من NOTAMs النشطة المؤثرة على العمليات.',
        'تؤخذ بيانات الطقس بالاعتبار في قرارات الإقلاع.'
      ],
      byRole: {
        instructor: {
          overview: 'إيجاز طقس وإشعارات قبل رحلات تدريبك اليومية.',
          steps: [
            'ابحث بالـ ICAO أو اختر مطاراً من الاختيارات السريعة.',
            'راجع METAR و TAF و NOTAMs قبل الإقلاع.',
            'استخدم هذه الصفحة لقرارات الذهاب / عدم الذهاب.'
          ]
        },
        student: {
          overview: 'تعلّم قراءة تقارير METAR و TAF الحقيقية وتحقق من الإشعارات قبل رحلتك.',
          steps: [
            'أدخل ICAO مطار الإقلاع أو الوصول.',
            'راجع METAR و TAF الخامين — نفس تنسيق امتحانات GACA.',
            'تحقق من الإشعارات لمعرفة ما هو مغلق أو مقيد.'
          ]
        }
      }
    },
    '/billing': {
      title: 'الفواتير',
      overview: 'إنشاء وإدارة فواتير الطلاب مع احتساب ضريبة القيمة المضافة وامتثال ZATCA.',
      steps: [
        'إنشاء فواتير بالبنود وضريبة القيمة المضافة والخصومات.',
        'تتبع حالة الدفع: غير مدفوعة، جزئية، مدفوعة.',
        'إنشاء PDF للفواتير للطلاب.'
      ],
      byRole: {
        student: {
          overview: 'اعرض الفواتير الصادرة لك من المدرسة وحالة الدفع.',
          steps: [
            'افتح الفاتورة لعرض البنود والضريبة والمبلغ المستحق.',
            'حمّل نسخة PDF لسجلاتك.',
            'لأي نزاع في الدفع تواصل مع القسم المالي عبر الإدارة والاتصالات.'
          ]
        }
      }
    },
    '/flight-logs': {
      title: 'سجلات الطيران',
      overview: 'تسجيل وإدارة عمليات الطيران بتفاصيل كاملة.',
      steps: [
        'تسجيل الرحلات بـ PIC والطالب والطائرة والمسار والمدة.',
        'تتبع ساعات الطيران بحسب المعلم والطالب.',
        'إنشاء تقارير ساعات الطيران للامتثال التنظيمي.'
      ],
      byRole: {
        instructor: {
          overview: 'سجّل الرحلات التي تدرّبها وراجع إجمالي ساعاتك كمعلم.',
          steps: [
            'انقر "تسجيل رحلة" لإدخال رحلة جديدة بـ PIC والطالب والطائرة والمسار والوقت.',
            'يُغذى الإدخال تلقائياً في ساعات المعلم الشهرية.',
            'استخدم الفلاتر لمراجعة رحلات طالب معين قبل درسه.'
          ]
        },
        student: {
          overview: 'سجل الرحلات الخاص بك — يسجلها معلمك بعد كل درس.',
          steps: [
            'هذه الصفحة للقراءة فقط — معلمك يسجل الرحلات بعد كل درس.',
            'استخدم زر التحميل لتصدير سجلك كـ PDF.',
            'إذا بدا إدخال خاطئاً تواصل مع معلمك للتصحيح.'
          ]
        }
      }
    },
    '/flight-dispatch': {
      title: 'الإقلاع',
      overview: 'لوحة إقلاع لحجز الخانات وإدارة العمليات اليومية.',
      steps: [
        'عرض لوحة الإقلاع بالخانات الزمنية والطائرات.',
        'حجز رحلات جديدة باختيار الطائرة والمعلم والطالب والوقت.',
        'تحقق من الطقس والإشعارات قبل الإقلاع.'
      ],
      byRole: {
        instructor: {
          overview: 'نسّق خانات رحلاتك — شاهد الطائرات المعيّنة والطلاب المحجوزين وانتقل مباشرة للتسجيل.',
          steps: [
            'راجع خانات اليوم المعيّنة لك على لوحة الإقلاع.',
            'انقر "تسجيل الرحلة" في خانة مكتملة لتعبئة نموذج السجل مسبقاً.',
            'تحقق دائماً من METAR/NOTAMs في صفحة الطقس قبل الرحلة.'
          ]
        }
      }
    },
    '/certifications': {
      title: 'الشهادات',
      overview: 'إصدار شهادات المقررات ودبلومات تخرج البرامج، وتتبع التجديدات عبر دورة امتثال GACA الكاملة.',
      steps: [
        'إصدار شهادة مقرر من ملف الطالب بعد أن تتحول قائمة التحقق إلى اللون الأخضر — محرك الأهلية يمنع الإصدار إذا كانت الساعات أو الامتحانات أو الاختبار العملي أو الحضور أو التخليص المالي غير مكتملة. يمكن للمسؤولين "الإصدار القسري" بمبرر موقع (مسجل في التدقيق).',
        'إصدار دبلوم تخرج البرنامج فقط بعد أن تمتلك جميع المقررات الإلزامية شهاداتها الخاصة. الدبلوم يوسم التسجيل كـ "مكتمل" ويعرض PDF دبلوم أفقي A4 مميز.',
        'سلسلة التجديد: عند تجديد شهادة، تُنشأ شهادة RENEW-YYYY-XXX جديدة وتُعلّم القديمة "مجددة" — سلسلة تدقيق كاملة محفوظة.',
        'بطاقة تجديد الشهادات في لوحة التحكم (فحص يومي 05:15 UTC) تعلّم الشهادات التي تنتهي خلال 90/60/30 يوماً وتنشئ مهمة امتثال عند 30 يوماً. تجديد بنقرة واحدة من البطاقة.',
        'يمكن للطلاب رفع شهادات طيران خارجية للتحقق.'
      ],
      byRole: {
        student: {
          overview: 'ارفع شهاداتك الخارجية واطلع على حالة الشهادات الصادرة لك — مع تذكيرات التجديد القادمة.',
          steps: [
            'انقر "رفع شهادة" لإرسال شهادة خارجية للتحقق.',
            'تحقق من صلاحية / تاريخ انتهاء كل شهادة. الإشعارات ترسل قبل 90 و 60 و 30 يوماً من الانتهاء.',
            'الشهادات الصادرة من المدرسة بعد إكمال البرنامج تظهر هنا تلقائياً.',
            'عند تجديد شهادة، الشهادة السابقة تنتقل إلى "متجاوزة" والجديدة (RENEW-...) تحل محلها.'
          ]
        }
      }
    },
    '/alumni': {
      title: 'دليل الخريجين',
      overview: 'دليل ما بعد الإكمال لكل خريج — يتتبع BFR والطبي والتوظيف والتجديدات القادمة. مطلوب لعمليات تدقيق امتثال GACA.',
      steps: [
        'الخريج = أي طالب بتسجيل "مكتمل" أو دبلوم تخرج برنامج صادر. يُملأ تلقائياً دون حاجة لبذور يدوية.',
        'صفّ حسب حالة التوظيف، ابحث بالاسم / الرقم الأكاديمي / صاحب العمل، أو فعّل "الشهادات المنتهية فقط" للتركيز على التواصل للتجديد.',
        'انقر أي بطاقة خريج لفتح درج الملف الشخصي — الاتصال، سجل ما بعد الإكمال، وكل شهادة (نشطة + متجاوزة/مجددة).',
        'للمسؤولين تحرير سجل ما بعد الإكمال: تاريخ BFR (مراجعة الطيران لمدة سنتين)، انتهاء الطبي + الفئة (1/2/3)، التوظيف (الحالة/الشركة/الدور/تاريخ البدء)، رابط LinkedIn، ملاحظات. كل تعديل مسجل في التدقيق.',
        'بطاقات الإحصائيات تعلّم الشهادات الطبية المنتهية خلال 90 يوماً و BFRs المستحقة — نقطتا تفعيل التدريب المتكرر.'
      ],
      byRole: {
        instructor: {
          overview: 'تصفح خريجي المدرسة — دليل للقراءة فقط مع تنبيهات التجديد لتخطيط التدريب المتكرر.',
          steps: [
            'استخدم الفلاتر لإيجاد الخريجين حسب حالة التوظيف أو التجديدات القادمة.',
            'درج الملف الشخصي يعرض معلومات الاتصال والشهادات وتقسيم النشط/المتجاوز.',
            'تحرير بيانات الخريج للمسؤولين فقط.'
          ]
        },
    '/accounting': {
      title: 'مركز المحاسبة (لوحة المدير المالي)',
      overview: 'لوحة الأستاذ العام: ميزان المراجعة، الميزانية، التدفق النقدي، دليل الحسابات، القيود، إشعارات الائتمان، مراكز التكلفة، الذمم الدائنة، الحزمة المالية، إقفال السنة، وتنبيهات المالية (بما فيها انحراف الصرف والتأجيل).',
      steps: [
        'تبديل بين التقارير عبر شريط التبويبات. جميع البيانات من الأستاذ العام المزدوج — يجب أن يبقى ميزان المراجعة متوازناً.',
        'شريط انحراف الصرف الأجنبي (أعلى الصفحة): يعرض كل فاتورة/سند بسعر مثبت مع نسبة الانحراف. اضغط بطاقة للانتقال المباشر إلى المستند، أو أيقونة البريد لإرسال كشف حساب فوري.',
        'إشعارات الائتمان: إصدار إشعار ائتمان مقابل أي فاتورة مع سبب ZATCA إلزامي، ثم استرداد بطريقة (تحويل / نقد / بطاقة / محفظة). القيد العكسي يُنشر تلقائياً.',
        'التنبيهات: تنبيهات المالية غير المحلولة مرتبة حسب الأولوية. زر "تأجيل 24 ساعة" يخفيها من التغذية والبريد اليومي مع الاحتفاظ بها للتدقيق.',
        'إقفال السنة المالية: نقل الإيرادات والمصروفات إلى الأرباح المحتجزة.',
        'الحزمة المالية: تصدير PDF لحزمة مجلس الإدارة بضغطة واحدة للفترة المختارة.',
        'التسوية البنكية → سطور الكشف: استيراد كشف البنك (CSV من أي بنك — الأعمدة تُكتشف تلقائياً ويمكن إعادة ربطها — أو SWIFT MT940). المعاينة تُظهر التكرارات والسطور غير الصالحة والمطابقات المقترحة مع دفعات الطلاب / المصروفات (نفس المبلغ خلال ± أيام). للسطور غير المطابقة: "بحث عن مطابقة"، "ترحيل إلى الأستاذ" أو قبول اقتراح قاعدة؛ "إلغاء الربط" يعكس المطابقة.',
        'التسوية البنكية → القواعد: مثل "FUEL" (سحوبات) → 5100 وقود، مع مركز تكلفة اختياري وفصل ضريبة 15%. القواعد تقترح الحساب فقط — المالية تؤكد كل ترحيل بعد معاينة القيد (مدين/دائن).',
        'التسوية البنكية → تقرير التسوية: اختر الحساب البنكي والشهر وأدخل رصيد الكشف الختامي → الأستاذ مقابل البنك، إيداعات في الطريق، مدفوعات معلقة، بنود البنك غير المسجلة، الرصيد المعدّل والفرق. حمّل PDF أو "اعتمد واحفظ لقطة" للمدقق.',
        'إقفال نهاية الشهر: قائمة تحقق لكل شهر — خطوات تلقائية (اعتماد التسوية البنكية، سطور غير مطابقة، الإهلاك، الإيراد المؤجل، فواتير الموردين، العهدة النقدية، ضريبة الاستقطاع، إقرار ضريبة القيمة المضافة نهاية الربع، إقفال السنة في ديسمبر) وخطوات يدوية بملاحظات. "إصلاح" ينقلك للصفحة المناسبة. يمكن للمدير "قفل الشهر" عند 100% (فك القفل يتطلب سبباً). تصل المالية والمدراء تذكيرات في يوم العمل الثالث والسابع ما دامت هناك خطوات معلقة، مع زر "إرسال تذكير الآن".'
      ]
    },
    '/violations': {
      title: 'مخالفات الطلاب وتحليلات السلوك',
      overview: 'تسجيل مخالفات الزي والتأخر والغياب والسلوك؛ يراها الطالب فوراً في البوابة. تحليلات السلوك تُظهر أين تتصاعد المخالفات ليتدخل المسجّل مبكراً.',
      steps: [
        'تبويب المخالفات: "تسجيل مخالفة" → اختر الطالب والنوع (قابل للتهيئة) والشدة/النقاط والتاريخ والوصف. يصل الطالب إشعار ويجب أن يُقرّ بها في البوابة → السلوك. المدير يحلّ / يرفض / يحذف.',
        'التصعيد: عند تجاوز الطالب حدّ النقاط (لوحة التصعيد → الإعدادات) يُنشأ خطاب إنذار PDF أو إحالة للجنة تلقائياً ويُرسل بالبريد (عند تهيئة SendGrid).',
        'تبويب تحليلات السلوك: خريطة حرارية للحوادث أسبوعياً (مخالفات مسجلة + غياب غير معذور + تأخر) حسب الصف أو الدفعة أو المدرب. الأغمق = حوادث أكثر. "آخر 4 أسابيع" يُقارن بالأربعة السابقة؛ المثلث الأحمر = إنذار مبكر (ارتفاع ≥50% و≥3 حوادث).',
        'اضغط خلية لعرض حوادث ذلك الأسبوع؛ اضغط اسم الصف لتوزيع الأنواع. "الأكثر ارتفاعاً" يسرد المجموعات المتصاعدة.',
        'تسجيل تدخل: على الصف المختار اضغط "تسجيل تدخل" (حديث مع الصف، اجتماع مدرب، تواصل مع ولي الأمر، تغيير جدول، إرشاد…). نقطة خضراء تُعلّم الأسبوع؛ النتيجة (فعّال / بلا تغيير / أسوأ) تُحسب تلقائياً من حوادث 4 أسابيع بعد مقابل قبل.',
        'اقتراح الخطوة التالية: يرتّب الدليل أنواع التدخل حسب سجل نتائجكم ونوع المخالفة السائد وما جُرّب سابقاً؛ "استخدم" يملأ النموذج. "اشرح بالذكاء الاصطناعي" يضيف مبرراً مختصراً.',
        'الملخص الأسبوعي: كل اثنين 06:00 يصل المدراء (واختيارياً مدربي الصفوف المعلّمة) ملخص بالصفوف المتصاعدة والإنذارات الجديدة والتدخلات المفتوحة. معاينة أو "إرسال الآن" من اللوحة.'
      ],
      byRole: { instructor: { overview: 'يمكنك تسجيل مخالفات لطلابك وعرض الخريطة الحرارية والتدخلات ومعاينة الملخص. الحدود وإعدادات الملخص والحذف للمدير فقط.' } }
    },
    '/batch-timetable': {
      title: 'جداول الدفعات',
      overview: 'جدول أسبوعي متكرر لكل دفعة. توليد الجدول ينشئ الصفوف والجلسات اليومية تلقائياً ويراها الطلاب في بوابتهم.',
      steps: [
        'اختر الدفعة ثم أضف الفترات على شبكة الأسبوع (اليوم، الوقت، المقرر، المدرب، الموقع). تعارضات المدربين تُتخطى ويُبلَّغ عنها.',
        '"توليد" ينشئ صفاً لكل فترة وجلسة لكل تاريخ في الفصل؛ إعادة التوليد آمنة والفترات المحذوفة تُلغي جلساتها المستقبلية.',
        'مزامنة القوائم: الطلاب المضافون إلى الدفعة أو المحذوفون منها يُسجَّلون / يُزالون من صفوف الجدول تلقائياً.',
        'أرشف الجدول عند نهاية الفصل؛ قائمة الجلسات تعرض التقويم المولَّد.'
      ]
    },
    '/debrief': {
      title: 'تقييم الدرس (سجل التدريب الإلكتروني)',
      overview: 'تقييم الدرس عند الطائرة من الجهاز اللوحي. تقييم واحد يسجّل الدرجة وسجل الرحلة وسجل التدريب الموقّع في خطوة واحدة.',
      steps: [
        'اختر الطالب — يعرض النظام الدرس التالي غير المجتاز في المنهج (المرحلة، رقم الدرس، الهدف، معيار الإتمام).',
        'قيّم كل مناورة S (مُرضٍ) أو U (غير مُرضٍ) بالأزرار الكبيرة. أي U يجعل نتيجة الدرس غير مُرضية ويستلزم خطة علاجية.',
        'أدخل وقت الطيران والطائرة والهبوطات (دروس الطيران/المحاكي) أو وقت الدرس الأرضي، مع ملاحظاتك.',
        'التوقيع: ارسم توقيعك أو أدخل رقم التوقيع السري المكوّن من 4 أرقام (يُضبط من سجلات التدريب).',
        'إرسال → تُحفظ الدرجة ويُنشأ سجل الرحلة ويُنبّه الطالب للتوقيع. يُختم السجل (سلسلة SHA-256) بعد توقيع الطالب.',
        'بدون اتصال: تعمل الصفحة من الذاكرة المؤقتة ويُحفظ التقييم على الجهاز ويُزامن تلقائياً عند عودة الاتصال.',
        'بوابة الصلاحية: إذا انتهى الفحص الطبي أو الرخصة يظهر شريط أحمر ويُمنع الإرسال.'
      ]
    },
    '/training-records': {
      title: 'سجلات التدريب',
      overview: 'سجلات التدريب الإلكترونية لكل طالب بتوقيع المدرب والطالب، مع ختم السلامة وتصدير PDF والتحقق.',
      steps: [
        'فلتر حسب الطالب والحالة. اضغط صفاً لعرض السجل الكامل بدرجات المناورات والتوقيعات.',
        'PDF: ورقة درجات قابلة للطباعة بالتوقيعات ورمز السلامة — جاهزة لمفتش هيئة الطيران.',
        'التحقق من الختم: يعيد حساب SHA-256 ويؤكد عدم تغيير السجل منذ توقيع الطالب.',
        'ضبط رقم التوقيع السري: 4 أرقام للتوقيع بدون رسم، ويُسجّل في سجل التدقيق باسمك.'
      ],
      byRole: { student: { overview: 'سجل تدريبك الرسمي. السجلات التي قيّمها مدربك تظهر هنا لمراجعتها وتوقيعها.', steps: ['الشريط الكهرماني يوضح عدد السجلات بانتظار توقيعك — اضغط "مراجعة وتوقيع".', 'اقرأ الدرجات والملاحظات، أضف تعليقاً إن أردت، ثم ارسم توقيعك أو استخدم الرقم السري.', 'بعد التوقيع يُختم السجل ولا يمكن تغييره. حمّل PDF لملفك الشخصي.'] } }
    },
    '/auto-scheduler': {
      title: 'الجدولة الآلية',
      overview: 'ينشئ أسبوع تدريب كاملاً: كل طالب جاهز يحصل على درسه الجوي التالي مع مدرب متاح وطائرة صالحة ضمن حدود العمل.',
      steps: [
        'اختر بداية الأسبوع والاستراتيجية — الاستمرارية (المدرب الأساسي ونفس نوع الطائرة)، الاستخدام (ملء كل الطائرات)، أو التوازن (توزيع عادل للمدربين).',
        'حدود العمل (للمسؤول): الحد الأقصى لساعات/جلسات المدرب يومياً، رحلات الطالب يومياً/أسبوعياً، مدة الحجز، وقت التجهيز، نافذة اليوم وأيام العمل.',
        'اقترح الأسبوع → تظهر مسودة مجمّعة حسب اليوم مع سبب كل مطابقة. الطلاب المتجاوزون يُدرجون مع السبب (طبي، مالي، لا تسجيل، لا فترة).',
        'عدّل أي فترة أو احذفها ثم انشر → تُنشأ الحجوزات ويُنبّه الطلاب؛ التعارضات تُبلّغ ولا تُستبدل.',
        'المدربون منتهيو الصلاحية (طبي/رخصة) يُستبعدون تلقائياً. "فحص الطقس" يقارن كل فترة بحدود المرحلة عبر TAF؛ الفترات دون الحدود تُحجز عند النشر إلا بتجاوز مُبرَّر ومسجّل.'
      ]
    },
    '/utilization': {
      title: 'تحليلات الاستخدام',
      overview: 'ساعات كل طائرة، نسبة استخدام المدربين، أسباب الإلغاء، ومعدل تقدم الطلاب لأي فترة.',
      steps: [
        'اختر نطاق التاريخ أو إعداداً سريعاً 7/30/90 يوماً.',
        'ساعات الطائرات: الرمادي طائرات خاملة، الكهرماني في الصيانة.',
        'استخدام المدرب: الساعات المنفذة مقسومة على ساعات التوفر المُعلنة.',
        'الخريطة الحرارية: ساعات حسب يوم الأسبوع لكل طائرة — الخلايا الداكنة سعة غير مستخدمة.',
        'معدل الطلاب: ساعات/أسبوع مع مؤشرات (على المسار / بطيء / في خطر).'
      ]
    },
    '/finance-home': {
      title: 'الرئيسية المالية',
      overview: 'صفحة الانطلاق لفريق المالية: الوضع النقدي، أعمار الذمم المدينة، وفواتير الموردين المستحقة هذا الأسبوع — جميعها من نفس دفتر الأستاذ المستخدم في التقارير التفصيلية.',
      steps: [
        'الوضع النقدي (أخضر): أرصدة جميع حسابات النقد والبنوك في دليل الحسابات مع صافي التغير خلال 30 يوماً.',
        'أعمار الذمم المدينة (أزرق): فواتير الطلاب غير المسددة حسب الفترة (0-30 / 31-60 / 61-90 / +90) مع إجمالي أرصدة الرسوم. اضغط "التقرير" لعرض تقرير الذمم الكامل.',
        'المستحق هذا الأسبوع (كهرماني): فواتير الموردين المستحقة خلال 7 أيام، المتأخرة، المستحقة لاحقاً، وعدد الفواتير بانتظار الاعتماد.',
        'القائمتان أدناه ترتّبان أكثر فواتير الطلاب تأخراً (انتقل إلى المطالبات) والفواتير الواجب دفعها تالياً.',
        'الروابط السريعة في الأسفل تفتح أكثر وحدات المالية استخداماً. استخدم "تحديث" لإعادة جلب الأرقام.',
        'نصيحة — قائمة المالية والمحاسبة في الشريط الجانبي مقسمة إلى مجموعات؛ اضغط عنوان المجموعة لطيّها أو فتحها ويُحفظ اختيارك على هذا الجهاز.'
      ]
    },
    '/petty-cash': {
      title: 'العهد النقدية',
      overview: 'تتبع صناديق النقد الفعلية مع قيود مزدوجة وتسوية بمستوى تدقيق.',
      steps: [
        'أنشئ صندوقاً: الاسم، الموقع، أمين العهدة، السلفة، العملة. الرصيد يتحدث تلقائياً عند كل عملية.',
        'تعبئة (مدين نقد / دائن الصندوق الرئيسي 1010) ومصروف (مدين مصروف / دائن نقد) — الميزان يبقى متزناً.',
        'التسوية: أدخل العد الفعلي → يُنشر قيد الفرق. أي فرق فردي أو تراكمي ≥ 500 ريال يُنتج تنبيه finance_alert (يرتفع إلى CRITICAL عند الضعف).',
        'زر "طباعة تقرير الفروق": يفتح ملخصاً قابلاً للطباعة بجميع الصناديق وسجل التسوية وعدد النقص/الزيادة وحقول التوقيع.'
      ]
    },
    '/opening-balances': {
      title: 'معالج الأرصدة الافتتاحية',
      overview: 'تحميل أرصدة إقفال الفترة السابقة إلى السنة المالية الجديدة مع ضمانات القيد المزدوج.',
      steps: [
        'الخطوة 1: اختر تاريخ "كما في" (عادة آخر يوم في السنة السابقة).',
        'الخطوة 2: أدخل الأسطر يدوياً، الصق CSV، أو ارفع "قالب مُعبأ". "تحميل قالب CSV" يعيد ملفاً مُعبأ مسبقاً بكل حساب نشط.',
        'الخطوة 3: مقارنة — اضغط "معاينة الفروق مقابل الأرصدة الحالية" لعرض الرصيد الحالي والجديد لكل حساب مع تحذيرات على الحسابات التي بها أرصدة. للمدقق: تصدير CSV أو إنشاء رابط موقّع لمدة 24 ساعة يفتح بدون تسجيل دخول.',
        'الخطوة 4: نشر القيد — حساب "3900 حقوق ملكية الرصيد الافتتاحي" يوازن القيد. الاستيراد قابل للعكس عبر /opening-balances/reset (للمسؤول فقط).'
      ]
    },
    '/vendor-bills': {
      title: 'فواتير الموردين (AP مع مطابقة ثلاثية)',
      overview: 'سير عمل الذمم الدائنة الرسمي: أمر شراء ↔ إيصال استلام ↔ فاتورة، تتبع ضريبة المدخلات، متعدد العملات، ومعاينة فورية للمرفق.',
      steps: [
        'فاتورة جديدة: اختر المورد + البنود، الضريبة، PO/GRN. قائمة العملات تدعم SAR / USD / EUR / GBP / ZAR — شريط FX المباشر يعرض "1 USD = 3.75 SAR" وما يعادله بالريال.',
        'تثبيت سعر الصرف (عقد): فعّل الخيار لعقود المورد طويلة الأجل وأدخل السعر المتعاقد + تاريخ صلاحية. عند انتهاء الصلاحية يعود إلى السعر المباشر تلقائياً.',
        'المطابقة الثلاثية: "مطابقة" ثم "اعتماد" ثم "تعليم كمدفوع" — القيد ينشر بالريال دائماً (مدين مصروف / دائن نقد / ضريبة مدخلات اختيارية) مع تدوين العملة الأصلية والسعر والمصدر.',
        'أرفق فاتورة المورد الأصلية PDF أو صورة (20 ميجا حد أقصى). الصف يعرض صورة مصغرة قابلة للنقر — الصور تُعرض مباشرة، PDF يُعرض عبر عارض المتصفح.',
        'عند تنبيه انحراف الصرف: النقر من الشريط ينقلك للصف مع وميض كهرماني.'
      ]
    },
    '/wht': {
      title: 'ضريبة الاستقطاع',
      overview: 'وحدة الاستقطاع السعودية — نطاقات 5% (خدمات) / 15% (إتاوات، أرباح) مع شهادات وقيود مزدوجة.',
      steps: [
        'سجّل الدفعة: المورد، المبلغ الإجمالي، النسبة. الصافي والضريبة يُحسبان تلقائياً.',
        'الإرسال → قيد مدين مصروف / دائن بنك / دائن ضريبة استقطاع مستحقة. شهادة PDF تُنشأ للمورد.',
        'تتبع الحالة: معلق → مقدم لـ ZATCA → مدفوع.'
      ]
    },
    '/zakat': {
      title: 'حاسبة الزكاة',
      overview: 'حساب وعاء الزكاة و 2.5% للشركات السعودية (متوافق مع GAZT/ZATCA).',
      steps: [
        'أدخل مكونات الوعاء (حقوق الملكية، الاحتياطيات، المخصصات، ناقصاً الخصومات).',
        'الزكاة تُحسب تلقائياً بنسبة 2.5% من الوعاء المؤهل.',
        'حمّل نموذج إقرار الزكاة PDF لتقديمه لـ ZATCA.'
      ]
    },

        finance: {
          overview: 'عرض حاملي الشهادات المتخرجين — استخدم للمتابعة المالية وتوقع إيرادات التدريب المتكرر.',
          steps: [
            'صفّ حسب حالة التوظيف والشهادات المنتهية لترتيب أولويات التواصل.',
            'جميع حقول الخريجين للقراءة فقط للمالية؛ حوّل التعديلات للمسؤول.'
          ]
        }
      }
    },
    '/licenses': {
      title: 'تراخيص الهيئة',
      overview: 'تتبع العملية التنظيمية التي تلي دبلوم تخرج البرنامج — تراخيص الطيارين من GACA أو EASA أو FAA أو SACAA.',
      steps: [
        'انقر "+ طلب جديد" للبدء — اختر دبلوم الطالب، الهيئة (GACA / EASA / FAA / SACAA)، ونوع الرخصة (PPL / CPL / ATPL / IR / CFI / MEL / MEI). النظام يرفض إنشاء الطلب دون دبلوم صالح.',
        'انتقل عبر خط الأنابيب المكوّن من 7 حالات بنقرة واحدة: مسودة → توصية صادرة → مقدم للهيئة → قيد المراجعة → معتمد → صادر. الرفض يتطلب سبباً ويمكن إعادة فتحه للمسودة.',
        'تنزيل PDF خطاب التوصية من ATO (A4، سطر توقيع رئيس التدريب). PDFs المُنشأة أثناء حالة المسودة أو الرفض تُختم تلقائياً بعلامة مائية DRAFT/REJECTED باللون الأحمر القطري — لن ترسل مسودة للهيئة عن طريق الخطأ أبداً.',
        'تعليم "صادر" يتطلب رقم رخصة الهيئة + تاريخ الإصدار/الانتهاء. هذه المعلومات تُختم على شهادة الدبلوم المصدر (authority_license_number) لتكون سلسلة تدقيق التدريب→الرخصة مرئية للأبد.',
        'الطلاب يتلقون إشعاراً في كل مرحلة (توصية، تقديم، اعتماد، إصدار، رفض).'
      ],
      byRole: {
        student: {
          overview: 'تتبع طلب رخصتك مع الهيئة — من توصية المدرسة إلى إصدار الرخصة الرسمي.',
          steps: [
            'ترى فقط طلباتك الخاصة. افتح أي طلب لعرض الخط الزمني وتنزيل خطاب التوصية.',
            'معالم الحالة (توصية، تقديم، اعتماد، إصدار) تُطلق إشعارات.',
            'الإنشاء والتقدم والرفض للموظفين فقط — إذا كنت بحاجة لطلب جديد، اتصل بقسم التدريب.'
          ]
        },
        instructor: {
          overview: 'إنشاء وتقديم طلبات الرخصة لطلابك. الرفض بسبب عند رفض الهيئة.',
          steps: [
            'ابدأ من دبلوم طالب متخرج. اختر الهيئة + نوع الرخصة.',
            'تقدم عبر خط الأنابيب كلما تقدمت الأوراق — النظام يفرض الترتيب الصحيح.',
            'عند إصدار الهيئة للرخصة، سجّل رقم الرخصة + التواريخ كما هو مطبوع بالضبط.'
          ]
        },
        finance: {
          overview: 'رؤية للقراءة فقط لخط أنابيب رسوم الرخص — استخدم فلاتر الحالة لتوقع الطلبات الصادرة مقابل المعلقة.',
          steps: [
            'صفّ حسب الحالة / الهيئة لرؤية الطلبات النشطة.',
            'الإنشاء والتقدم للمسؤولين/المعلمين فقط.'
          ]
        }
      }
    },
    '/documents': {
      title: 'الوثائق',
      overview: 'إدارة الوثائق المركزية مع رفع وتصنيف.',
      steps: [
        'رفع وثائق مع وسوم الفئة والأوصاف.',
        'البحث والتصفية بالنوع والمالك والتاريخ.',
        'التحكم بالوصول حسب دور المستخدم.'
      ],
      byRole: {
        instructor: {
          overview: 'اطلع على الوثائق المشتركة مع المعلمين وارفع وثائق تخص تدريسك.',
          steps: [
            'استخدم الفلتر لإيجاد ما تبحث عنه.',
            'ارفع وثيقة بعنوان واضح ووسم تصنيف.',
            'ترى فقط الوثائق المخصصة للمعلمين أو للمدرسة كاملةً.'
          ]
        }
      }
    },
  },
};

/**
 * Returns a path→{title,overview,steps} object for the current language and
 * user role. When a `byRole[userRole]` override exists on an entry it is
 * shallow-merged over the default.
 */
const getHelpContent = (userRole) => {
  const lang = i18n.language?.startsWith('ar') ? 'ar' : 'en';
  const source = helpContent[lang] || helpContent.en;
  const result = {};
  for (const [path, entry] of Object.entries(source)) {
    if (entry.byRole && userRole && entry.byRole[userRole]) {
      result[path] = { ...entry, ...entry.byRole[userRole] };
      delete result[path].byRole;
    } else {
      const { byRole, ...rest } = entry;
      result[path] = rest;
    }
  }
  return result;
};

export default getHelpContent;
